/**
 * UPG Schema Migrations. Version-scoped maps for type renames, merges, and deprecations.
 * https://unifiedproductgraph.org/spec | MIT
 */

import { getScale } from './scales.js'

// ─── Migration map per version ─────────────────────────────────────────────────

export interface UPGTypeMigration {
  /** The old type name being retired */
  from: string
  /** The new canonical type name */
  to: string
  /** Default designation/property values to set on migrated nodes */
  defaults?: Record<string, unknown>
  /** Human-readable explanation of why this migration exists */
  reason: string
}

/**
 * Version-scoped migration definitions.
 * Key is the version that INTRODUCES the migration (target version).
 * Each entry describes how an old type maps to a new canonical type.
 */
export const UPG_MIGRATIONS: Record<string, UPGTypeMigration[]> = {
  '0.9.0': [
    // (since v0.9.0, UPG-660) theme → roadmap_theme. `theme` was the only bare,
    // unqualified theme among four domain-prefixed siblings (strategic_theme,
    // content_theme, feedback_theme); it claimed the unqualified word and read as
    // interchangeable with strategic_theme (the N6/UPG-652 confusion). Renamed to
    // roadmap_theme so all four read consistently and the customer-problem roadmap
    // grouping is explicit. Lifecycle-free, identical property surface (theme_scope
    // / priority); no property migration required. Paired edge renames live in
    // UPG_EDGE_MIGRATIONS['0.9.0'].
    {
      from: 'theme',
      to: 'roadmap_theme',
      reason: 'theme renamed to roadmap_theme (UPG-660). The bare "theme" was the only domain-unprefixed theme among strategic_theme / content_theme / feedback_theme; it grabbed the unqualified word and read as interchangeable with strategic_theme. roadmap_theme makes the customer-problem roadmap grouping explicit and consistent across the four. Same property surface (theme_scope / priority); no property migration needed.',
    },
  ],

  '0.7.0': [
    // (since v0.7.0, UPG-571) story_statement → user_story. The v0.2.7 split
    // correctly separated the templated promise from the engineering work
    // (task), but renamed the surviving statement half to the coined
    // `story_statement`. "User story" is the universally-recognised industry
    // term for exactly that artefact, and UPG's value is being the recognisable
    // canonical vocabulary; the statement is re-canonicalised under
    // `user_story`. Lifecycle-free, same property surface (as_a / i_want_to /
    // so_that / text); no property migration required. The paired `task` and
    // the `task_implements_*` / `epic_specified_by_*` / `*_verified_by_*` /
    // `test_case_covers_*` edges are renamed in UPG_EDGE_MIGRATIONS['0.7.0'].
    {
      from: 'story_statement',
      to: 'user_story',
      reason: 'story_statement re-canonicalised to user_story (UPG-571). The v0.2.7 Statement/Implementation split was sound (it extracted the lifecycle-bearing work into `task`), but the coined `story_statement` name raised the adoption barrier; "user story" is the industry-standard term for the templated promise. Lifecycle-free, identical property surface (as_a / i_want_to / so_that / text); no property migration needed.',
    },
  ],

  '0.4.0': [
    // (since v0.4.0) hypothesis_claim reverts to hypothesis (the simpler
    // canonical name ("claim" is implied). hypothesis_evidence deprecated
    // in favour of canonical evidence entity.
    {
      from: 'hypothesis_claim',
      to: 'hypothesis',
      reason: 'hypothesis_claim renamed back to hypothesis, the canonical name. The "claim" suffix was redundant; a hypothesis is a claim by definition. Properties (we_believe / will_result_in / we_know_when / risk_if_wrong / current_confidence) are identical; no property migration required.',
    },
    {
      from: 'hypothesis_evidence',
      to: 'evidence',
      reason: 'hypothesis_evidence collapsed into canonical evidence entity. evidence gains evidence_rigor (rigor axis) + evidence_source (source axis) + weight (UPGAssessment) to absorb both property surfaces. Relationship to hypothesis expressed via hypothesis_has_evidence edge.',
    },
    // (since v0.4.0) story_task collapsed into canonical task. Audit showed
    // story_task had no unique properties (estimate absorbed into task), no unique
    // edges (story_task_implements_story_statement → task_implements_story_statement),
    // and identical lifecycle (both use WORK_ITEM_TEMPLATE). The additive-vocabulary
    // rule says: if the second half of a split adds no new shape, reuse the existing
    // type. The user_story replacement updates from story_task to task, and the
    // chain collapses.
    {
      from: 'story_task',
      to: 'task',
      reason: 'story_task collapsed into canonical task. Both use the WORK_ITEM lifecycle, neither carries unique properties (estimate is absorbed into task), and no unique edges (implements_story_statement works on task). When two types share the same shape, the additive-vocabulary rule keeps one.',
    },
  ],

  '0.2.8': [
    // hypothesis decomposes into hypothesis_claim
    // (P5 templated-statement, the stable belief) + hypothesis_evidence
    // (P2 scored-assessment, n rows of accruing evidence). Superseded by
    // the v0.4.0 rename of hypothesis_claim back to hypothesis, which also
    // collapses hypothesis_evidence into evidence. The 1→1 alias below
    // is retained for loaders targeting v0.2.8 files; the '0.4.0' aliases
    // above handle the subsequent hop for any system applying migrations in
    // version order.
    {
      from: 'hypothesis',
      to: 'hypothesis_claim',
      reason: 'hypothesis decomposes into hypothesis_claim (templated belief, lifecycle-bearing) + hypothesis_evidence (n rows of accruing evidence, lifecycle-free). Superseded at v0.4.0 by the rename: hypothesis_claim → hypothesis. Apply migrations in version order; a v0.2.8 file will migrate hypothesis → hypothesis_claim → hypothesis, which resolveEntityType collapses to a single hop.',
    },
  ],

  '0.1.0': [
    // ── User layer: pain_point + user_need → need ──────────────────────────
    {
      from: 'pain_point',
      to: 'need',
      defaults: { valence: 'pain' },
      reason: 'Consolidated into neutral "need" type with valence property. Framework labels provide context-specific display names (Problem in Lean Canvas, Struggle in JTBD, etc.)',
    },
    {
      from: 'user_need',
      to: 'need',
      defaults: { valence: 'gap' },
      reason: 'Consolidated into "need" type. user_need was semantically identical to pain_point with different framing.',
    },

    // ── UX Research: insight consolidation ──────────────────────────────────
    {
      from: 'research_insight',
      to: 'insight',
      defaults: {},
      reason: 'Already deprecated. Research insights are insights with research provenance.',
    },
    {
      from: 'finding',
      to: 'insight',
      defaults: { insight_level: 'finding' },
      reason: 'Already deprecated. Findings are insights at the "finding" level.',
    },
    {
      from: 'ux_insight',
      to: 'insight',
      defaults: { source_domain: 'ux' },
      reason: 'UX insights are insights with source_domain=ux. Not a distinct type.',
    },
    {
      from: 'highlight',
      to: 'observation',
      defaults: { is_highlighted: true },
      reason: 'Already deprecated. Highlights are flagged observations.',
    },

    // ── Metrics consolidation ──────────────────────────────────────────────
    {
      from: 'kpi',
      to: 'metric',
      defaults: { designation: 'kpi' },
      reason: 'KPI is a metric role (designation), not a distinct type. Metric.designation already supports "kpi".',
    },
    {
      from: 'north_star_metric',
      to: 'metric',
      defaults: { designation: 'north_star' },
      reason: 'Already deprecated. North star is a metric designation.',
    },
    {
      from: 'input_metric',
      to: 'metric',
      defaults: { designation: 'input' },
      reason: 'Already deprecated. Input metric is a metric designation.',
    },
    {
      from: 'metric_definition',
      to: 'metric',
      reason: 'A metric definition is a metric without implementation. Lifecycle state, not a separate type. (Corrected in v0.26.0: the default previously stamped `has_implementation: false`, a property `metric` has never declared. Dropped rather than declared, because a metric with no live reading simply has no `current_value`, and a boolean restating that would immediately drift from it.)',
    },

    // ── Experiment consolidation ────────────────────────────────────────────
    {
      from: 'ab_test',
      to: 'experiment_run',
      defaults: { experiment_type: 'ab_test' },
      reason: 'A/B test is an experiment_run with method=a_b_test. The run carries actual lift, observed reach, and disposition. retargeted from `experiment` to `experiment_run` (each is a run instance, not a planning artefact).',
    },
    {
      from: 'growth_experiment',
      to: 'experiment_run',
      defaults: { experiment_type: 'growth' },
      reason: 'Growth experiment is an experiment_run in growth context. retargeted from `experiment` to `experiment_run` to give loaders a single retarget hop instead of two-hopping through deprecated `experiment`.',
    },
    {
      from: 'pricing_experiment',
      to: 'experiment_run',
      defaults: { experiment_type: 'pricing' },
      reason: 'Pricing experiment is an experiment_run about pricing. retargeted from `experiment` to `experiment_run` (the run is where the data lives).',
    },

    // ── Cross-domain consolidations ────────────────────────────────────────
    {
      from: 'risk_item',
      to: 'risk',
      defaults: { risk_type: 'program' },
      reason: 'risk_item (Program Mgmt) is identical to risk (Legal). Consolidated onto the single kind axis, `risk_type`. (Corrected in v0.26.0: the default previously named `risk_domain`, a property `risk` has never declared; `program` was added to the `risk_type` enum rather than giving risk a second, drift-prone classification vocabulary.)',
    },
    {
      from: 'security_incident',
      to: 'incident',
      defaults: { incident_type: 'security' },
      reason: 'Security incident is an incident with incident_type=security. Same structure, different context.',
    },
    {
      from: 'defect_report',
      to: 'support_ticket',
      defaults: { ticket_type: 'bug' },
      reason: 'Defect report is a support ticket with ticket_type=bug. Same signal interface. (Corrected: the default previously named `ticket_designation`, a property `support_ticket` has never declared, so every migrated node was stamped with an undeclared field.)',
    },
    {
      from: 'onboarding_flow',
      to: 'user_flow',
      defaults: { flow_type: 'onboarding' },
      reason: 'Onboarding flow is a user flow with flow_type=onboarding. Same structure.',
    },
    {
      from: 'nps_score',
      to: 'nps_campaign',
      defaults: {},
      reason: 'NPS score demoted to a property of nps_campaign, not a standalone entity.',
    },
  ],

  // v0.2.6: no 1→1 type renames. The experiment split
  // is purely additive in v0.2.6 (new entity types `experiment_plan` +
  // `experiment_run` and 4 new edges). The 1→N split rule for migrating
  // legacy `experiment` rows lives in UPG_SPLIT_MIGRATIONS['0.2.6'] below.
  // The 1→1 alias from `experiment` to its canonical replacement is
  // deferred to v0.2.7 alongside the broader deprecation + edge retarget
  // pass.

  '0.2.0': [
    // ── User layer: jtbd → job ──────────────────────────────────────────────
    {
      from: 'jtbd',
      to: 'job',
      reason: 'Renamed for clarity. "job" is the standard JTBD shorthand; "jtbd" was the framework acronym used as a type name.',
    },

    // ── Design layer: how_might_we → design_question ────────────────────────
    {
      from: 'how_might_we',
      to: 'design_question',
      reason: 'Renamed to a framework-neutral name. "how_might_we" was HMW-format specific; design_question covers all design-framing question types.',
    },

    // ── Decision consolidation ──────────────────────────────────────────────
    {
      from: 'design_decision',
      to: 'decision',
      defaults: { layer: 'design' },
      reason: 'design_decision is a decision with layer=design. Consolidated into the single decision type with layer property.',
    },
    {
      from: 'architecture_decision',
      to: 'decision',
      defaults: { layer: 'engineering' },
      reason: 'architecture_decision is a decision with layer=engineering. Consolidated into the single decision type with layer property.',
    },
    {
      from: 'product_decision',
      to: 'decision',
      defaults: { layer: 'product' },
      reason: 'product_decision is a decision with layer=product. Consolidated into the single decision type with layer property.',
    },

    // ── DevOps: acronym expansions ──────────────────────────────────────────
    {
      from: 'sli',
      to: 'service_level_indicator',
      reason: 'Expanded from acronym to full name for readability and discoverability.',
    },
    {
      from: 'slo',
      to: 'service_level_objective',
      reason: 'Expanded from acronym to full name for readability and discoverability.',
    },
    {
      from: 'sla',
      to: 'service_level_agreement',
      reason: 'Expanded from acronym to full name for readability and discoverability.',
    },

    // ── Business Model: _bm suffix removal ────────────────────────────────
    {
      from: 'customer_segment_bm',
      to: 'market_segment',
      reason: 'Removed _bm suffix. Originally renamed to target_customer_segment, then consolidated into market_segment in v0.2.0.',
    },
    {
      from: 'channel_bm',
      to: 'distribution_channel',
      reason: 'Removed _bm suffix. Renamed to distribution_channel, which is more descriptive than the abbreviation.',
    },

    // ── Growth domain: disambiguate generic names ──────────────────────────
    {
      from: 'campaign',
      to: 'growth_campaign',
      reason: 'Too generic; conflicted with marketing_campaign_plan and nps_campaign. Prefixed with growth to clarify domain scope.',
    },
    {
      from: 'segment',
      to: 'behavioral_segment',
      reason: 'Too generic; conflicted with market_segment and target_customer_segment. Renamed to behavioral_segment to clarify this is a behaviour-based user slice.',
    },

    // ── Pricing consolidation ───────────────────────────────────────────
    {
      from: 'package',
      to: 'pricing_tier',
      reason: 'Package and pricing_tier describe the same concept (the plan a customer buys). Consolidated into pricing_tier as the central pricing entity.',
    },

    // ── GTM restructure ─────────────────────────────────────────────
    {
      from: 'target_customer_segment',
      to: 'market_segment',
      reason: 'Target customer segment and market segment describe the same concept, a slice of the addressable market. Consolidated into market_segment.',
    },

    // ── Content consolidation ───────────────────────────────────────
    {
      from: 'internal_doc',
      to: 'document',
      reason: 'Internal doc is a document with audience=internal. Consolidated into document with expanded document_type enum.',
    },
  ],
}

// ─── Migration helpers ─────────────────────────────────────────────────────────

/**
 * Compare two semver-style version strings of the form `MAJOR.MINOR.PATCH`.
 *
 * Returns a negative number if `a < b`, zero if `a === b`, positive if `a > b`.
 * Components are compared numerically (e.g. `0.2.11` is greater than `0.2.8`,
 * not less; naive string comparison gets this backwards because `'11' < '8'`
 * lexicographically).
 *
 * Used by every range-filtered migration helper below
 * (`getMigrationMap`, `getAllMigrations`, `getPropertyMigrations`,
 * `getSplitMigrations`, `getUPGEdgeMigrations`, `migrateNodeProperties`).
 *
 * Previously, those helpers used raw string comparison and silently dropped
 * every v0.2.7+v0.2.8 rule once `UPG_VERSION` crossed `0.2.10` fix.
 *
 * @example
 * compareVersions('0.2.8', '0.2.11')  // → negative (0.2.8 < 0.2.11)
 * compareVersions('0.2.11', '0.2.8')  // → positive (0.2.11 > 0.2.8)
 * compareVersions('0.2.0', '0.2.0')   // → 0
 */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((n) => Number.parseInt(n, 10))
  const pb = b.split('.').map((n) => Number.parseInt(n, 10))
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const da = pa[i] ?? 0
    const db = pb[i] ?? 0
    if (Number.isNaN(da) || Number.isNaN(db)) {
      // Fall back to string comparison for non-numeric components (e.g. pre-release tags).
      const sa = a.split('.')[i] ?? ''
      const sb = b.split('.')[i] ?? ''
      if (sa < sb) return -1
      if (sa > sb) return 1
      continue
    }
    if (da !== db) return da - db
  }
  return 0
}

/** True if `version` is in the half-open range `(fromVersion, toVersion]`. */
function versionInRange(version: string, fromVersion: string, toVersion: string): boolean {
  return compareVersions(version, fromVersion) > 0 && compareVersions(version, toVersion) <= 0
}

/**
 * Build a flat old→new type name map for a specific version upgrade.
 * Used by TYPE_ALIASES and .upg file readers.
 *
 * @example
 * // Upgrading from v0 (pre-UPG) to v0.2.0: collect every type rename.
 * const map = getMigrationMap('0.0.0', '0.2.0')
 * // map.pain_point === 'need'
 * // map.jtbd       === 'job'
 * // map.package    === 'pricing_tier'
 */
export function getMigrationMap(
  fromVersion: string,
  toVersion: string,
): Record<string, string> {
  const map: Record<string, string> = {}
  // Collect all migrations between fromVersion and toVersion
  for (const [version, migrations] of Object.entries(UPG_MIGRATIONS)) {
    if (versionInRange(version, fromVersion, toVersion)) {
      for (const m of migrations) {
        map[m.from] = m.to
      }
    }
  }
  return map
}

/**
 * Apply migrations to a single node, converting its type and
 * merging default properties.
 *
 * `T` is the caller's node shape, typically `UPGBaseNode` or a narrower
 * app-specific node type. The constraint (`{ type: string; properties?: ... }`)
 * keeps the function node-library-agnostic so adapters, CLI tools, and test
 * fixtures can all reuse it without coupling to `UPGBaseNode` directly.
 *
 * @example
 * // Legacy deprecated type migrates to its canonical replacement.
 * const legacy = { id: 'n1', type: 'pain_point', properties: { description: 'Onboarding is slow' } }
 * const migrated = migrateNode(legacy, '0.0.0', '0.1.0')
 * // migrated.type               === 'need'
 * // migrated.properties.valence === 'pain'       // merged default
 * // migrated.properties.description === 'Onboarding is slow'  // original preserved
 *
 * @example
 * // Non-deprecated type passes through; return shape preserves T.
 * const healthy = { id: 'n2', type: 'persona', properties: { is_primary: true } }
 * const same = migrateNode(healthy, '0.0.0', '0.1.0')
 * // same === healthy  (referentially unchanged)
 */
export function migrateNode<T extends { type: string; properties?: Record<string, unknown> }>(
  node: T,
  fromVersion: string,
  toVersion: string,
): T {
  const migrations = getAllMigrations(fromVersion, toVersion)
  const migration = migrations.find((m) => m.from === node.type)
  if (!migration) return node

  return {
    ...node,
    type: migration.to,
    properties: {
      ...migration.defaults,
      ...node.properties,
    },
  }
}

/**
 * Get all migration entries between two versions.
 */
function getAllMigrations(fromVersion: string, toVersion: string): UPGTypeMigration[] {
  const result: UPGTypeMigration[] = []
  for (const [version, migrations] of Object.entries(UPG_MIGRATIONS)) {
    if (versionInRange(version, fromVersion, toVersion)) {
      result.push(...migrations)
    }
  }
  return result
}

/**
 * Get all deprecated type names (across all versions) as a flat set.
 * Useful for validation warnings.
 *
 * @example
 * const deprecated = getDeprecatedTypes()
 * deprecated.has('pain_point')    // → true (consolidated into 'need')
 * deprecated.has('jtbd')          // → true (renamed to 'job')
 * deprecated.has('persona')       // → false (still canonical)
 */
export function getDeprecatedTypes(): Set<string> {
  const deprecated = new Set<string>()
  for (const migrations of Object.values(UPG_MIGRATIONS)) {
    for (const m of migrations) {
      deprecated.add(m.from)
    }
  }
  return deprecated
}

// ─── Property migrations ──────────────────────────────────
//
// When a type's property surface shifts (drops, renames at the top level,
// lifts from `properties` to top-level `UPGBaseNode` slots, or self-referential
// cleanup), `UPGPropertyMigration` records the change so loaders can clean up
// existing data with one consistent contract instead of each consumer
// inventing its own rules.
//
// History:
// - (v0.2.2) split `metric`: 14 intra-`properties` drops.
// - (v0.2.8) drop `hypothesis.properties.we_test_by`.
// - (v0.2.13) extended to a discriminated union covering top-level
//   field drift surfaced by Wave 3 dogfood: `lifecycle_status → status`,
//   `properties.stage → status` lift, self-referential `source_id` /
//   `source_type` cleanup.
// - (v0.2.14) widen `rename_top_level` to `outcome` (167 residual
//   top_level_drift rows in unified-product-graph.upg after v0.2.13 pass).
//
// **Discriminated-union contract.** Each rule carries a `kind` discriminator
// mirroring `UPGEdgeMigration`. The four kinds are
// orthogonal; a rule does one thing. Compose multiple rules per (version,
// type) when more than one kind applies.

/**
 * A property-level migration applied at load time.
 *
 * Discriminated by `kind`:
 *
 * - `'drop_props'`: remove keys from `node.properties`. The original
 *   shape; the existing v0.2.2 + v0.2.8 entries use this.
 * - `'rename_top_level'`: rename a top-level `UPGBaseNode` field to a
 *   different top-level field, optionally remapping its value. Used when a
 *   pre-canonical custom field (e.g. `lifecycle_status`) shifts to its
 *   canonical slot (`status`).
 * - `'lift_property_to_top_level'`: move a value from `node.properties` to a
 *   top-level field, optionally remapping. Used when a slot that was
 *   pragmatically stuffed in `properties` graduates to a top-level
 *   `UPGBaseNode` slot (e.g. `properties.stage` → top-level `status`).
 * - `'drop_when_self_referential'`: drop top-level fields whose value
 *   equals the node's own `id` or `type` (or another configured
 *   self-reference). Used to clean up redundant round-trip metadata that's
 *   only meaningful when pointing OUT of the node, not at itself.
 */
export type UPGPropertyMigration =
  | {
      kind: 'drop_props'
      /** The entity type whose properties are being migrated. */
      type: string
      /** Property keys to drop from `type.properties`. */
      drop_props: string[]
      /** Human-readable explanation surfaced in load-time warnings. */
      reason: string
    }
  | {
      kind: 'rename_top_level'
      /** The entity type this rule applies to (or `'*'` for all types). */
      type: string
      /** Pre-canonical top-level field name. */
      from: string
      /** Canonical top-level field name. */
      to: string
      /**
       * Optional value remap from old → new. If unset, the value is copied
       * verbatim from `from` to `to`. If set, only entries in the map are
       * remapped; values absent from the map pass through unchanged.
       */
      value_map?: Record<string, string>
      /** Human-readable explanation surfaced in load-time warnings. */
      reason: string
    }
  | {
      kind: 'lift_property_to_top_level'
      /** The entity type this rule applies to (or `'*'` for all types). */
      type: string
      /** Key inside `properties` that should move to top-level. */
      from_property: string
      /** Top-level field that receives the lifted value. */
      to: string
      /** Optional value remap from old → new (same semantics as `rename_top_level`). */
      value_map?: Record<string, string>
      /** Human-readable explanation surfaced in load-time warnings. */
      reason: string
    }
  | {
      kind: 'drop_when_self_referential'
      /** The entity type this rule applies to (or `'*'` for all types). */
      type: string
      /**
       * Top-level fields to inspect. Each is dropped only when its value
       * equals the node's `id` (for fields named like `*_id`) or `type`
       * (for fields named like `*_type`); otherwise preserved.
       */
      fields: string[]
      /** Human-readable explanation surfaced in load-time warnings. */
      reason: string
    }
  | {
      kind: 'remap_property_value'
      /** The entity type this rule applies to (or `'*'` for all types). */
      type: string
      /** Key inside `properties` whose value is being remapped (stays in the bag). */
      property: string
      /**
       * Old to new value remap. Only entries in the map are touched; values
       * absent from the map pass through unchanged.
       */
      value_map: Record<string, string>
      /**
       * Optional sibling property to receive the mapped value (a split). When
       * set, the mapped value is written to `to_property` and `property` is
       * reset to `reset_value`. Used to separate a conflated axis, e.g. lift a
       * data_flow orientation value out of `direction` into `orientation`.
       */
      to_property?: string
      /** Value written back to `property` after a split. Required when `to_property` is set. */
      reset_value?: string
      /** Human-readable explanation surfaced in load-time warnings. */
      reason: string
    }
  | {
      /**
       * Reshape a bare numeric property into a `UPGAssessment` object
       * (`{ value, label, scale_id }`). For the v0.10.0 property-registry
       * correction (#43): interfaces that always declared `UPGAssessment` were
       * generated loosely, so pre-0.10.0 graphs stored a bare number (e.g.
       * `market_trend.impact: 4`). This wraps that number on its canonical scale,
       * deriving the qualitative `label` from the scale's points. Idempotent: a
       * value that is already an object (assessment) passes through untouched.
       */
      kind: 'reshape_value_to_assessment'
      /** The entity type this rule applies to (or `'*'` for all types). */
      type: string
      /** Key inside `properties` whose bare number is wrapped (stays in the bag). */
      property: string
      /** The assessment scale to label the value on (e.g. `impact_5`, `importance_5`). */
      scale_id: string
      /** Human-readable explanation surfaced in load-time warnings. */
      reason: string
    }

/**
 * Version-scoped property migrations. Same convention as `UPG_MIGRATIONS`:
 * the key is the version that introduces the migration.
 */
export const UPG_PROPERTY_MIGRATIONS: Record<string, UPGPropertyMigration[]> = {
  // ── v0.35.0: risk.probability → risk.likelihood, STAGED ─────────────────────
  //
  // Deliberately EMPTY, and the emptiness is the design rather than an omission.
  // The rename is staged: `RiskProperties.probability` is kept, marked
  // `@deprecated since="0.35.0" removeIn="1.0.0"`, and still read. 0.35.0 changes
  // what writers emit (`likelihood`) and what readers prefer; it changes no
  // stored bytes, so there is nothing for a load-time rule to do. A graph
  // written before 0.35.0 carries `probability` and no `likelihood` and reads
  // correctly — which is what staging BUYS, and a rule here would spend it.
  //
  // Three reasons there is no executable rule, in order of weight:
  //
  //  1. A staged deprecation that rewrites data is not staged. The point of the
  //     `epic.estimate` → `effort` pattern this follows is that both names live
  //     through the window and the consumer chooses; migrating the key on load
  //     would end the window on the first read and break any reader still
  //     asking for the old name.
  //  2. The executable step belongs at the REMOVAL version, not here. That is
  //     the two-step the `removeIn="0.5.0"` properties followed: declared
  //     deprecated with a deadline at 0.4.0, dropped by `drop_props` rules in
  //     the '0.5.0' block below. `probability` is scheduled for the same
  //     treatment at 1.0.0.
  //  3. No kind fits, and inventing one would be the larger change. Checked
  //     against the APPLIER, not just the type union:
  //       - `drop_props` deletes, which is the 1.0.0 step, not this one.
  //       - `lift_property_to_top_level` targets `UPGBaseNode`, and `likelihood`
  //         is a property, not a top-level field.
  //       - `rename_top_level` renames a top-level field, same mismatch.
  //       - `remap_property_value` is the near miss and still misses twice: it
  //         `break`s unless `typeof oldValue === 'string'` (these values are
  //         `UPGAssessment` objects, or bare numbers in older graphs), and its
  //         `to_property` split OVERWRITES the source with `reset_value`, which
  //         is precisely the staging this release is buying.
  //       - `reshape_value_to_assessment` wraps a bare number on a named scale;
  //         it does not move a key.
  //     The edge-side `kind: 'rename'` (UPG_EDGE_MIGRATIONS, e.g. 0.8.0's
  //     `theme_groups_feature` -> `roadmap_theme_groups_feature`) renames an
  //     EDGE TYPE across instances and has no property-key analogue. Adding one
  //     is new spec surface: a ratified decision, not a side effect of one
  //     property rename.
  //
  // The scale moves ride along and need no rule either. `PROPERTY_SCALE_MAP`
  // resolves `likelihood` to `likelihood_5` (which carries `threat.likelihood`
  // off `confidence_5` in the same line, since the map is keyed by name) and
  // `PROPERTY_SCALE_MAP_BY_ENTITY` resolves `risk.impact` to `severity_5`. Both
  // resolve at READ time, and an assessment only overrides them by PINNING a
  // `scale_id` on the stored value. Measured across all 36 `.upg` files in the
  // repo: zero assessments pin a `scale_id` on `likelihood`, `probability` or
  // `impact`, so there is nothing stamped to re-stamp. One that does pin a
  // `scale_id` keeps it, by the existing contract.
  //
  // What lands at 1.0.0, written down now so it is not rediscovered:
  //   { kind: 'drop_props', type: 'risk', drop_props: ['probability'],
  //     reason: '1.0.0: risk.probability was @deprecated since 0.35.0 with
  //              removeIn="1.0.0"; superseded by `likelihood` on likelihood_5.' }
  '0.35.0': [],
  '0.32.0': [
    // C3 — archive becomes a base-node axis. WorkspaceProperties.archived /
    // archived_at shipped the orthogonal-archive idea for exactly one type;
    // field data showed it is general (a tracker held 559 archived-completed
    // items beside 18 live-completed ones), so the pair moves to UPGBaseNode
    // and the workspace properties are @deprecated. Lift, not drop: the value
    // is real and its new home means the same thing.
    { kind: 'lift_property_to_top_level', type: 'workspace', from_property: 'archived', to: 'archived',
      reason: '0.32.0: archive generalised from WorkspaceProperties to UPGBaseNode.archived. Same fact, wider home; the workspace property is @deprecated.' },
    { kind: 'lift_property_to_top_level', type: 'workspace', from_property: 'archived_at', to: 'archived_at',
      reason: '0.32.0: pairs with the archived lift above. UPGBaseNode.archived_at is the canonical home.' },
    // C4 — the duplicate label surfaces. TaskProperties.labels and
    // BugProperties.labels were declared "applied uniformly across work item
    // types" and duplicated base-node `tags` with no consumer anywhere. Three
    // label surfaces (base tags, per-type tags, per-type labels) is two too
    // many; grouped labels are classification_axis + classification_value, and
    // ungrouped ones are `tags`.
    { kind: 'drop_props', type: 'task', drop_props: ['labels'],
      reason: '0.32.0: TaskProperties.labels duplicated base-node `tags` and had no consumers. Freeform labels live in `tags`; grouped labels are classification_axis + node_classified_as_classification_value.' },
    { kind: 'drop_props', type: 'bug', drop_props: ['labels'],
      reason: '0.32.0: BugProperties.labels duplicated base-node `tags` and had no consumers. Freeform labels live in `tags`; grouped labels are classification_axis + node_classified_as_classification_value.' },
  ],
  // ── v0.21.0: UPG-690 D.1 — collapse residual *_status shadows into status ────
  //
  // Six `*_status` enum properties that re-encode their entity's own lifecycle
  // phases (the residual Pattern-D shadows not caught by the 0.15.0 sweep, now
  // that the entities sit on shared templates). Each is removed; the authored
  // value LIFTS to UPGBaseNode.status, remapping where the property vocabulary
  // diverged from the (template) lifecycle. `security_policy.policy_status` was
  // deliberately EXCLUDED (Captain 2026-07-04): its active/under_review/retired
  // values are an operational-status axis distinct from its APPROVAL lifecycle
  // (active != approved) — a distinct-axis keeper, not a shadow.
  '0.21.0': [
    { kind: 'lift_property_to_top_level', type: 'brand_identity', from_property: 'brand_stage', to: 'status',
      reason: 'UPG-690 D.1: brand_stage (exploratory/defined/mature) is an exact shadow of the brand_identity lifecycle. Removed in 0.21.0; lifts to status verbatim.' },
    { kind: 'lift_property_to_top_level', type: 'deliverable', from_property: 'deliverable_status', to: 'status',
      value_map: { not_started: 'todo', accepted: 'done', rejected: 'done' },
      reason: 'UPG-690 D.1: deliverable_status re-encoded the deliverable WORK_ITEM lifecycle. Removed in 0.21.0; lifts to status with not_started -> todo, accepted/rejected -> done.' },
    { kind: 'lift_property_to_top_level', type: 'press_release', from_property: 'pr_status', to: 'status',
      value_map: { in_review: 'review', distributed: 'published' },
      reason: 'UPG-690 D.1: pr_status re-encoded the press_release PUBLISHING lifecycle. Removed in 0.21.0; lifts to status with in_review -> review, distributed -> published.' },
    { kind: 'lift_property_to_top_level', type: 'program', from_property: 'program_status', to: 'status',
      value_map: { on_hold: 'paused', cancelled: 'sunset' },
      reason: 'UPG-690 D.1: program_status re-encoded the program OPERATIONAL lifecycle. Removed in 0.21.0; lifts to status with on_hold -> paused, cancelled -> sunset.' },
    { kind: 'lift_property_to_top_level', type: 'project', from_property: 'project_status', to: 'status',
      value_map: { on_hold: 'paused', cancelled: 'sunset' },
      reason: 'UPG-690 D.1: project_status re-encoded the project OPERATIONAL lifecycle. Removed in 0.21.0; lifts to status with on_hold -> paused, cancelled -> sunset.' },
    { kind: 'lift_property_to_top_level', type: 'screen', from_property: 'screen_status', to: 'status',
      reason: 'UPG-690 D.3/Q3: screen flips from MATURITY to a hand-authored build-pipeline lifecycle (draft/in_design/built/shipped/deprecated); screen_status already carried those exact values. Removed in 0.21.0; lifts to status verbatim.' },
  ],
  // ── v0.16.0: Pattern G — open-standard data boundary (UPG-691) ───────────────
  //
  // The portable graph holds modeling knowledge, not PII, registry filings,
  // app-session ids, or live vendor figures. These flowed through every export /
  // snapshot / sync. They are dropped from the body and belong to the hosting
  // app's identity / tenancy / measurement layer instead (boundary ADR
  // 2026-06-16-open-standard-data-boundary). The infra-pointer URLs that stay
  // (repo_url, storage_uri, *.logo_url, help_video.url, ...) were marked
  // `modifier:'volatile'` in this release rather than dropped, so a consumer
  // knows they are environment-specific and may be stripped on export. The
  // `owner: string` -> ownership-edge migration (C1) is a separate, larger wave
  // (0.17.0). (FALSE-POSITIVE guard kept: specification.spec_url, design_token.value,
  // vulnerability.cve_id, screen.route, bug.environment -- content / standard refs.)
  '0.16.0': [
    { kind: 'drop_props', type: 'person', drop_props: ['email'],
      reason: 'UPG-691 boundary: person.email is PII for the hosting app identity layer, not the portable graph. Reference the modeled actor by edge; do not inline contact details.' },
    { kind: 'drop_props', type: 'contact', drop_props: ['email', 'phone'],
      reason: 'UPG-691 boundary: contact.email / contact.phone are PII for the app identity layer, not the portable graph.' },
    { kind: 'drop_props', type: 'document', drop_props: ['author'],
      reason: 'UPG-691 boundary: document.author was free-text name/email (PII). The authoring actor is referenced by edge, not inlined.' },
    { kind: 'drop_props', type: 'legal_entity', drop_props: ['tax_id', 'registration_number', 'principal_address'],
      reason: 'UPG-691 boundary: government / registry identifiers belong to the tenancy / records system, not the open interchange body.' },
    { kind: 'drop_props', type: 'investigation', drop_props: ['session_id'],
      reason: 'UPG-691 boundary: investigation.session_id is an app-runtime / routing artifact, not modeling knowledge.' },
    { kind: 'drop_props', type: 'workspace', drop_props: ['slug'],
      reason: 'UPG-691 boundary: workspace.slug is an app-routing artifact, not modeling knowledge.' },
    { kind: 'drop_props', type: 'organization', drop_props: ['billing_plan'],
      reason: 'UPG-691 boundary: organization.billing_plan is a SaaS-account/tenancy concept. Model it via subscription / pricing_tier, not on the structural entity.' },
    { kind: 'drop_props', type: 'account', drop_props: ['annual_revenue'],
      reason: 'UPG-691 boundary: account.annual_revenue is live firmographic vendor data. Route live figures to a metric node, not the definition entity.' },
    { kind: 'drop_props', type: 'ai_model', drop_props: ['cost_per_1k_tokens'],
      reason: 'UPG-691 boundary: ai_model.cost_per_1k_tokens is a live vendor price. Route it to a metric / record entity, not the model definition.' },
  ],
  // ── v0.15.0: Pattern D — collapse *_status shadows into the lifecycle ─────────
  //
  // 14 `*_status` enum properties whose values ARE the entity's own lifecycle
  // phase set (UPG-689 Pattern D, the T1.1 guardrail set). They re-encoded the
  // lifecycle as a property, shadowing the base-node `status` field that the
  // lifecycle already drives. Each property is removed and any authored value
  // LIFTS to `UPGBaseNode.status` (same convention as 0.9.14 kr_status). Two
  // diverge in vocabulary from their own lifecycle and remap on lift:
  //   - marketing_channel.channel_status: deprecated -> sunset
  //   - security_audit.audit_status: scheduled -> planning, in_progress -> active
  '0.15.0': [
    { kind: 'lift_property_to_top_level', type: 'agent_definition', from_property: 'agent_status', to: 'status',
      reason: 'UPG-689 Pattern D: agent_status duplicated the agent_definition lifecycle (testing/active/disabled). Removed in 0.15.0; the authored value lifts to UPGBaseNode.status.' },
    { kind: 'lift_property_to_top_level', type: 'data_pipeline', from_property: 'pipeline_status', to: 'status',
      reason: 'UPG-689 Pattern D: pipeline_status duplicated the data_pipeline lifecycle (active/paused/failed/deprecated). Removed in 0.15.0; lifts to status.' },
    { kind: 'lift_property_to_top_level', type: 'database_schema', from_property: 'migration_status', to: 'status',
      reason: 'UPG-689 Pattern D: migration_status duplicated the database_schema lifecycle (current/pending/failed). Removed in 0.15.0; lifts to status.' },
    { kind: 'lift_property_to_top_level', type: 'deployment', from_property: 'deploy_status', to: 'status',
      reason: 'UPG-689 Pattern D: deploy_status duplicated the deployment lifecycle (rolling/success/failure). Removed in 0.15.0; lifts to status.' },
    { kind: 'lift_property_to_top_level', type: 'external_api', from_property: 'api_status', to: 'status',
      reason: 'UPG-689 Pattern D: api_status duplicated the external_api lifecycle (beta/active/deprecated/unavailable). Removed in 0.15.0; lifts to status.' },
    { kind: 'lift_property_to_top_level', type: 'feature_area', from_property: 'area_status', to: 'status',
      reason: 'UPG-689 Pattern D: area_status duplicated the feature_area lifecycle (planned/active/deprecated). Removed in 0.15.0; lifts to status.' },
    { kind: 'lift_property_to_top_level', type: 'feature_flag', from_property: 'flag_status', to: 'status',
      reason: 'UPG-689 Pattern D: flag_status duplicated the feature_flag lifecycle (off/rollout/on). Removed in 0.15.0; lifts to status.' },
    { kind: 'lift_property_to_top_level', type: 'objective', from_property: 'objective_status', to: 'status',
      reason: 'UPG-689 Pattern D: objective_status duplicated the objective lifecycle (active/achieved/deferred). Removed in 0.15.0; lifts to status.' },
    { kind: 'lift_property_to_top_level', type: 'outcome', from_property: 'outcome_status', to: 'status',
      reason: 'UPG-689 Pattern D: outcome_status duplicated the outcome lifecycle (identified/measuring/achieved/abandoned). Removed in 0.15.0; lifts to status.' },
    { kind: 'lift_property_to_top_level', type: 'prototype', from_property: 'test_status', to: 'status',
      reason: 'UPG-689 Pattern D: test_status duplicated the prototype lifecycle (untested/testing/passed/failed). Removed in 0.15.0; lifts to status.' },
    { kind: 'lift_property_to_top_level', type: 'review_gate', from_property: 'gate_status', to: 'status',
      reason: 'UPG-689 Pattern D: gate_status duplicated the review_gate lifecycle (pending/approved/rejected/bypassed). Removed in 0.15.0; lifts to status.' },
    { kind: 'lift_property_to_top_level', type: 'roadmap_item', from_property: 'item_status', to: 'status',
      reason: 'UPG-689 Pattern D: item_status duplicated the roadmap_item lifecycle (planned/in_progress/shipped/deferred). Removed in 0.15.0; lifts to status.' },
    { kind: 'lift_property_to_top_level', type: 'marketing_channel', from_property: 'channel_status', to: 'status',
      value_map: { deprecated: 'sunset' },
      reason: 'UPG-689 Pattern D: channel_status (active/paused/deprecated) re-encoded the marketing_channel lifecycle (planning/active/paused/completed/sunset). Removed in 0.15.0; lifts to status with deprecated -> sunset.' },
    { kind: 'lift_property_to_top_level', type: 'security_audit', from_property: 'audit_status', to: 'status',
      value_map: { scheduled: 'planning', in_progress: 'active' },
      reason: 'UPG-689 Pattern D: audit_status (scheduled/in_progress/completed) re-encoded the security_audit lifecycle (planning/active/paused/completed/sunset). Removed in 0.15.0; lifts to status with scheduled -> planning, in_progress -> active.' },
  ],
  // ── v0.14.0: Pattern E — remove deprecated/ghost properties (UPG-574) ────────
  //
  // Properties marked deprecated (some "Removed in 0.9.1") that were never
  // actually removed, plus superseded soft-aliases. The RICE / opportunity-sizing
  // scoring inputs were framework-scoped from 0.9.0: their value lives on the
  // `framework_exercise` includes-edge of the rice-scoring / opportunity-sizing
  // framework, not on the entity. The remaining four are superseded by a sibling
  // (cause_category, rule_strength, size_estimate, effort). Loaders drop the
  // stranded keys; the values were never canonical on the entity.
  '0.14.0': [
    { kind: 'drop_props', type: 'opportunity', drop_props: ['reach', 'frequency', 'pain', 'opportunity_score'],
      reason: 'UPG-574: opportunity scoring (reach/frequency/pain/opportunity_score) was framework-scoped from 0.9.0 (opportunity-sizing). Apply the framework and read the value off the framework_exercise edge; the entity props are removed in 0.14.0.' },
    { kind: 'drop_props', type: 'solution', drop_props: ['reach', 'impact', 'confidence', 'effort', 'rice_score'],
      reason: 'UPG-574: RICE inputs (reach/impact/confidence/effort) and the computed rice_score were framework-scoped from 0.9.0 (rice-scoring). Apply the framework and read the score off the framework_exercise edge; the entity props are removed in 0.14.0.' },
    { kind: 'drop_props', type: 'root_cause', drop_props: ['category', 'verified'],
      reason: 'UPG-574: root_cause.category was a legacy free-form field superseded by the closed-enum cause_category; verified duplicated cause_confidence === confirmed. Both removed in 0.14.0.' },
    { kind: 'drop_props', type: 'design_guideline', drop_props: ['strictness'],
      reason: 'UPG-574: design_guideline.strictness was superseded by rule_strength (a richer imperative-force enum). Removed in 0.14.0.' },
    { kind: 'drop_props', type: 'behavioral_segment', drop_props: ['size'],
      reason: 'UPG-574: behavioral_segment.size was a v0.2 alias for size_estimate. Removed in 0.14.0; use size_estimate.' },
    { kind: 'drop_props', type: 'task', drop_props: ['estimate'],
      reason: 'UPG-574: task.estimate was a story_task-collapse relic that duplicated effort. Removed in 0.14.0; use effort.' },
  ],
  // ── v0.12.0: P14 Bucket C — drop the one non-derived denormalized aggregate ──
  //
  // The count caches (feature_count, evidence_count, workflow step_count /
  // agent_count) are KEPT — 0.11.6 marked them `modifier:'derived'` (the
  // sanctioned "derived cache" reframe), so tooling knows to recompute rather
  // than removing them. `business_model.monetisation_basis` is NOT a count: it's
  // a categorical rollup of the model's revenue_stream.billing_model children, so
  // it drops here. `pattern` stays (a chosen archetype, not a rollup).
  // (customer_health_score.metrics — a metric→value map — is deferred: dropping
  // it is lossy until the per-metric values move onto the composed_of_metric
  // edges; tracked as a follow-up.)
  '0.12.0': [
    { kind: 'drop_props', type: 'business_model', drop_props: ['monetisation_basis'],
      reason: 'P14 Bucket C: monetisation_basis is a categorical rollup of the model’s revenue_stream.billing_model children, not a first-class attribute. Drop it; KEEP `pattern` (a chosen archetype).' },
  ],
  // ── v0.10.2: market_intelligence number -> assessment reshape ──────────────
  //
  // The v0.10.0 property-registry generator fix (#43) corrected several property
  // types that interfaces already declared as `UPGAssessment` but the generator
  // had emitted loosely. A pre-0.10.0 graph that stored a bare number for
  // `market_trend.impact` / `relevance` is now invalid (the schema wants
  // `{value,label}`). These rules wrap the bare number on its canonical scale so
  // the migration is one command, not a hand-reshape. The enum tightenings the
  // same #43 fix surfaced (data_flow.direction, integration_pattern.pattern_type,
  // api_contract.protocol, service.service_type) already have rules under 0.9.12;
  // 0.10.2 also makes ALL of them visible to validate_graph (they were applied by
  // migrate_properties but never surfaced as a fixable drift).
  '0.10.2': [
    {
      kind: 'reshape_value_to_assessment',
      type: 'market_trend',
      property: 'impact',
      scale_id: 'impact_5',
      reason:
        'market_trend.impact is a UPGAssessment (impact_5); the 0.10.0 generator fix (#43) tightened it from a bare number. A legacy numeric value wraps to {value,label} on impact_5.',
    },
    {
      kind: 'reshape_value_to_assessment',
      type: 'market_trend',
      property: 'relevance',
      scale_id: 'importance_5',
      reason:
        'market_trend.relevance is a UPGAssessment (importance_5); the 0.10.0 generator fix (#43) tightened it from a bare number. A legacy numeric value wraps to {value,label} on importance_5.',
    },
  ],
  // ── v0.9.14: drop the key_result kr_status / status twin (A2) ──────────────
  //
  // key_result carried BOTH a lifecycle `status` and a `kr_status` property with
  // the identical four-value enum (on_track | at_risk | behind | achieved) ==
  // the KEY_RESULT_LIFECYCLE phases. Two fields, same values: an authoring trap.
  // The redundant property is removed; the authored health value lifts to the
  // canonical `UPGBaseNode.status` so a graph that set `kr_status` stays correct.
  '0.9.14': [
    {
      kind: 'lift_property_to_top_level',
      type: 'key_result',
      from_property: 'kr_status',
      to: 'status',
      reason:
        'A2 (0.9.14). key_result.kr_status duplicated the lifecycle `status` with the identical enum (on_track | at_risk | behind | achieved). The twin is removed; the authored health value lifts to `UPGBaseNode.status` verbatim.',
    },
  ],
  // ── v0.9.12: technical-domain enum widening + data_flow orientation split ──
  //
  // Real authoring outgrew four engineering-domain enums. 0.9.12 widens them
  // (see properties/domains/engineering.ts) and adds a data_flow `orientation`
  // axis distinct from `direction` (cardinality). These rules dual-read the
  // legacy values so graphs authored before 0.9.12 stay valid on load:
  //   - data_flow.direction inbound/outbound/internal -> properties.orientation,
  //     and direction resets to unidirectional (all three were one-way).
  //   - integration_pattern.pattern_type "data sync" -> data_sync,
  //     backend_client -> client_library.
  //   - service.service_type backend -> api (use the new `cli` for CLI tools).
  //   - api_contract.protocol HTTP -> REST, HTTP/SSE -> the new SSE value.
  '0.9.12': [
    {
      kind: 'remap_property_value',
      type: 'data_flow',
      property: 'direction',
      value_map: { inbound: 'inbound', outbound: 'outbound', internal: 'internal' },
      to_property: 'orientation',
      reset_value: 'unidirectional',
      reason:
        'data_flow.direction conflated cardinality with orientation; the orientation values move to the new `orientation` property and direction resets to `unidirectional` (0.9.12).',
    },
    {
      kind: 'remap_property_value',
      type: 'integration_pattern',
      property: 'pattern_type',
      value_map: { 'data sync': 'data_sync', backend_client: 'client_library' },
      reason:
        'integration_pattern.pattern_type widened in 0.9.12; legacy "data sync" snake-cases to data_sync and backend_client collapses to client_library.',
    },
    {
      kind: 'remap_property_value',
      type: 'service',
      property: 'service_type',
      value_map: { backend: 'api' },
      reason:
        'service.service_type `backend` is too generic to canonise; normalised to `api` in 0.9.12 (use `cli` for command-line tools).',
    },
    {
      kind: 'remap_property_value',
      type: 'api_contract',
      property: 'protocol',
      value_map: { HTTP: 'REST', 'HTTP/SSE': 'SSE' },
      reason: 'api_contract.protocol normalised in 0.9.12: HTTP maps to REST and HTTP/SSE maps to the new SSE value.',
    },
  ],
  // ── v0.8.0: UPG-574 deprecated-property removal pass ───────────────────
  //
  // The seven properties tagged `@deprecated since v0.4.0` that survived the
  // v0.5.0 hygiene pass are removed from the spec in v0.8.0 (a breaking
  // release). Five of the seven already carry a migration:
  //   - `task.task_status` / `bug.bug_status` lift to `UPGBaseNode.status`
  //     (see `UPG_PROPERTY_MIGRATIONS['0.4.0']`, values identical).
  //   - `learning.metric` drops in favour of the `learning_observed_on_metric`
  //     edge (see `UPG_PROPERTY_MIGRATIONS['0.5.0']`).
  // The two remaining property surfaces are migrated here:
  //   - `evidence.strength` (simple `strong | moderate | weak` enum) is
  //     dropped in favour of `weight: UPGAssessment` (scale `weight_5pt`).
  //     Authors carrying the legacy key should copy the value to `weight`
  //     before applying this migration: `strong → 5`, `moderate → 3`,
  //     `weak → 1`, with the old word preserved in `weight.label`.
  //   - `story_task.estimate` / `.effort` / `.priority` drop. `story_task`
  //     itself is deprecated (`UPG_MIGRATIONS['0.4.0']` renames it to the
  //     canonical `task`); the type rename copies every property verbatim,
  //     so the values survive on `task` (`TaskProperties.estimate` /
  //     `.effort` / `.priority`, all live). This rule removes the residue
  //     from any node still typed `story_task` at migration time.
  '0.8.0': [
    {
      kind: 'drop_props',
      type: 'evidence',
      drop_props: ['strength'],
      reason: 'UPG-574 (v0.8.0). evidence.strength was `@deprecated since v0.4.0`; the structured `weight: UPGAssessment` (scale `weight_5pt`) replaces the `strong | moderate | weak` enum to align with the spec-wide scoring pattern. Authors should copy the value to `weight` before applying this migration (strong -> 5, moderate -> 3, weak -> 1; carry the old word in `weight.label`). The migration drops the residue.',
    },
    {
      kind: 'drop_props',
      type: 'story_task',
      drop_props: ['estimate', 'effort', 'priority'],
      reason: 'UPG-574 (v0.8.0). story_task.estimate / .effort / .priority were `@deprecated since v0.4.0`; story_task itself is deprecated (UPG_MIGRATIONS["0.4.0"] renames it to canonical `task`, copying all properties verbatim), so the values survive on TaskProperties.estimate / .effort / .priority (all live). This rule removes the residue from any node still typed `story_task` at migration time.',
    },
  ],

  // ── v0.5.0: UPG-509 deprecation hygiene pass ───────────────────────────
  //
  // Properties tagged `@deprecated since="0.4.0" removeIn="0.5.0"` (and the
  // sibling prose `since v0.4.0, removed in v0.5.0` aliases) are removed
  // from `UPG_PROPERTY_SCHEMA` in v0.5.0. This block migrates existing graphs
  // that still carry the values:
  //
  //   1. `*_status` lifecycle properties → lift to `UPGBaseNode.status` per
  //      `status-convention.md` Rule 1. Value maps preserve every legacy
  //      lifecycle phase verbatim; the property and the canonical slot used
  //      the same enum, so the lift is lossless.
  //   2. Free-text properties replaced by canonical edges (`winner`,
  //      `customer`, `consumers`, `recipients`, `metric`) → drop. The
  //      structural relationship now lives on a typed edge (e.g.
  //      `learning_observed_on_metric`); the free-text echo is residue.
  //   3. Properties renamed to a sibling field (`root_cause.confidence` →
  //      `cause_confidence`; `metric.frequency` → `cadence`;
  //      `key_activity.frequency` / `symptom.frequency` /
  //      `churn_reason.frequency` → `frequency_rating` + `frequency_count` +
  //      `frequency_period`) → drop. The canonical replacements were added
  //      in v0.4.0; the legacy field is removed here.
  //
  // Rule kinds chosen per case: `lift_property_to_top_level` for status
  // lifts, `drop_props` for both free-text-replaced-by-edge and
  // renamed-to-sibling cases. The CHANGELOG entry for v0.5.0 enumerates the
  // full mapping.
  '0.5.0': [
    // ── 1. Status lifts (lift_property_to_top_level) ─────────────────────
    {
      kind: 'lift_property_to_top_level',
      type: 'strategic_theme',
      from_property: 'theme_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). theme_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status` per status-convention.md Rule 1. Values (`proposed | active | achieved | abandoned`) preserved verbatim.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'assumption',
      from_property: 'validation_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). validation_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status`. Values (`unvalidated | validating | validated | invalidated`) preserved verbatim.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'service',
      from_property: 'service_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). service_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status`. Values (`development | staging | production | deprecated`) preserved verbatim. The separate `lifecycle` field (service maturity) stays as a typed property.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'api_contract',
      from_property: 'contract_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). api_contract.contract_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status`. Values (`draft | published | deprecated`) preserved verbatim.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'technical_debt_item',
      from_property: 'debt_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). debt_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status`. Values (`identified | acknowledged | scheduled | in_progress | resolved`) preserved verbatim.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'investigation',
      from_property: 'investigation_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). investigation_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status`. Values (`open | active | paused | resolved | abandoned`) preserved verbatim.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'fix',
      from_property: 'fix_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). fix_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status`. Values (`planned | in_progress | deployed | verified | reverted`) preserved verbatim.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'contract',
      from_property: 'contract_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). legal.contract.contract_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status`. Values (`draft | in_review | signed | active | expired | terminated`) preserved verbatim.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'threat_model',
      from_property: 'threat_model_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). threat_model_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status`. Values (`draft | in_review | approved | stale`) preserved verbatim.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'role',
      from_property: 'role_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). role_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status`. Values (`active | backfilling | dormant | retired`) preserved verbatim.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'team_okr',
      from_property: 'okr_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). okr_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status`. Values (`draft | committed | active | complete | abandoned`) preserved verbatim.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'partnership',
      from_property: 'partnership_status',
      to: 'status',
      reason: 'UPG-509 (v0.5.0). partnership_status was `@deprecated removeIn="0.5.0"` since v0.4.0; canonical lifecycle belongs on `UPGBaseNode.status`. Values (`proposed | active | paused | ended`) preserved verbatim.',
    },
    // ── 2. Free-text replaced by canonical edges (drop_props) ────────────
    {
      kind: 'drop_props',
      type: 'model_comparison',
      drop_props: ['winner'],
      reason: 'UPG-509 (v0.5.0). model_comparison.winner was `@deprecated removeIn="0.5.0"` since v0.4.0; the canonical edge `model_comparison_winner_is_ai_model` carries the structural relationship. Free-text echo dropped on load.',
    },
    {
      kind: 'drop_props',
      type: 'service_level_agreement',
      drop_props: ['customer'],
      reason: 'UPG-509 (v0.5.0). service_level_agreement.customer was `@deprecated removeIn="0.5.0"` since v0.4.0; the canonical edge `service_level_agreement_covers_account` carries the counterparty relationship. Free-text echo dropped on load.',
    },
    {
      kind: 'drop_props',
      type: 'data_product',
      drop_props: ['consumers'],
      reason: 'UPG-509 (v0.5.0). data_product.consumers was `@deprecated removeIn="0.5.0"` since v0.4.0; the canonical edge `data_product_consumed_by_service` carries each consumer. Free-text echo dropped on load.',
    },
    {
      kind: 'drop_props',
      type: 'report',
      drop_props: ['recipients'],
      reason: 'UPG-509 (v0.5.0). report.recipients was `@deprecated removeIn="0.5.0"` since v0.4.0; the canonical edge `report_distributed_to_team` carries each recipient. Free-text echo dropped on load.',
    },
    {
      kind: 'drop_props',
      type: 'learning',
      drop_props: ['metric'],
      reason: 'UPG-509 (v0.5.0). learning.metric was `@deprecated removeIn="0.5.0"` since v0.4.0; the canonical edge `learning_observed_on_metric` carries the metric relationship. Free-text echo dropped on load.',
    },
    // ── 3. Renamed-to-sibling properties (drop_props) ────────────────────
    {
      kind: 'drop_props',
      type: 'root_cause',
      drop_props: ['confidence'],
      reason: 'UPG-509 (v0.5.0). root_cause.confidence was `@deprecated removed in v0.5.0` since v0.4.0; renamed to `cause_confidence` to disambiguate from the spec-wide `UPGAssessment` confidence axis. Values are unchanged; authors that still carry the legacy key should copy the value to `cause_confidence` before applying this migration. The migration drops the residue.',
    },
    {
      kind: 'drop_props',
      type: 'metric',
      drop_props: ['frequency'],
      reason: 'UPG-509 (v0.5.0). metric.frequency (typed `MetricFrequency`) was `@deprecated removed in v0.5.0` since v0.4.0; replaced by `cadence` (typed `Cadence`). Migration: `realtime → continuous`, all other values map 1:1 to the cadence enum. Authors that still carry the legacy key should copy the value to `cadence` (with the realtime→continuous remap) before applying this migration.',
    },
    {
      kind: 'drop_props',
      type: 'key_activity',
      drop_props: ['frequency'],
      reason: 'UPG-509 (v0.5.0). key_activity.frequency (typed `string`) was `@deprecated removed in v0.5.0` since v0.4.0; replaced by the canonical 4-way frequency split (`cadence`, `frequency_count` + `frequency_period`, or `frequency_rating`).',
    },
    {
      kind: 'drop_props',
      type: 'symptom',
      drop_props: ['frequency'],
      reason: 'UPG-509 (v0.5.0). symptom.frequency was `@deprecated removed in v0.5.0` since v0.4.0; replaced by `frequency_rating` (qualitative tier), `frequency_count` + `frequency_period` (exact rate), or the `Cadence` primitive. Migration of legacy values: `once → rare`, `sporadic → occasional`, `frequent → regular`, `constant → constant`.',
    },
    {
      kind: 'drop_props',
      type: 'churn_reason',
      drop_props: ['frequency'],
      reason: 'UPG-509 (v0.5.0). churn_reason.frequency (typed `number`) was `@deprecated removed in v0.5.0` since v0.4.0; replaced by `frequency_count` + `frequency_period` (exact rate over a known period) or `frequency_rating` (qualitative tier).',
    },
  ],

  '0.4.0': [
    // deprecate task_status + bug_status; lift to UPGBaseNode.status.
    // Values are identical to the canonical lifecycle phases; no value_map needed.
    {
      kind: 'lift_property_to_top_level',
      type: 'task',
      from_property: 'task_status',
      to: 'status',
      value_map: {
        todo:        'todo',
        in_progress: 'in_progress',
        in_review:   'in_review',
        done:        'done',
      },
      reason: 'task_status duplicates UPGBaseNode.status. Canonical lifecycle field wins; property-level shadow deprecated. Values are identical; lift is lossless.',
    },
    {
      kind: 'lift_property_to_top_level',
      type: 'bug',
      from_property: 'bug_status',
      to: 'status',
      value_map: {
        open:        'open',
        in_progress: 'in_progress',
        fixed:       'fixed',
        verified:    'verified',
        wont_fix:    'wont_fix',
      },
      reason: 'bug_status duplicates UPGBaseNode.status. Canonical lifecycle field wins; property-level shadow deprecated. Values are identical; lift is lossless.',
    },
  ],

  '0.2.8': [
    {
      kind: 'drop_props',
      type: 'hypothesis',
      drop_props: [
        // `we_test_by` described the experimental
        // method legacy hypothesis rows intended to use to test themselves.
        // That concept now lives on the paired `experiment_plan.method`
        // (hypothesis_claim links to the plan via
        // `hypothesis_claim_requires_experiment_plan`). Drop the property
        // from legacy nodes during migration; the value can be reconstructed
        // by walking the requires_experiment_plan edge if present, or
        // discarded if no plan was created.
        'we_test_by',
      ],
      reason: 'hypothesis decomposes into hypothesis_claim + hypothesis_evidence. The legacy `we_test_by` property described experimental method, which now lives on the linked experiment_plan.method via the canonical `hypothesis_claim_requires_experiment_plan` edge.',
    },
  ],

  '0.2.2': [
    {
      kind: 'drop_props',
      type: 'metric',
      drop_props: [
        // Moved to metric_quality_assessment. Authors should create
        // a sibling assessment node and link via
        // metric_assessed_by_metric_quality_assessment.
        'quality_correlated', 'quality_actionable', 'quality_sensitive',
        'quality_comparative', 'quality_related', 'quality_score',
        'proxy_reason', 'proxy_confidence', 'proxy_alternatives',
        // Out of canonical spec scope; tool runtime state belongs in
        // tool-extension namespaces (e.g. extensions.entopo.metric_sync).
        'external_metric_id', 'external_query', 'last_synced_at',
        'sync_status', 'sync_error',
      ],
      reason: 'metric decomposed. Quality/proxy props moved to metric_quality_assessment; sync state moved to tool extensions.',
    },
  ],

  // ── top-level field drift surfaced by Wave 3 dogfood ──────────────
  // The two production graphs in this repo (`.upg/entopo.upg` and
  // `.upg/unified-product-graph.upg`) carry pre-canonical node shapes.
  // The four rules below close the drift catalog:
  //
  //   1. `product.properties.stage` (pre-canonical "idea / build /
  //      launched" enum stuffed inside properties) → top-level `status`
  //      (canonical `UPGProductStage` enum: `concept | validation | build |
  //      beta | launch | growth | mature | maintenance | sunset`).
  //   2. `product.lifecycle_status` (pre-canonical top-level field with
  //      "draft / active" values) → top-level `status` with the same
  //      `UPGProductStage` enum.
  //   3. Self-referential `source_id` / `source_type` cleanup; these
  //      fields are for round-trip from external imports (Notion / Linear);
  //      when they self-reference, they're redundant noise.
  //   4. `hypothesis_claim.properties.status` → top-level `status` with
  //      cross-lifecycle remap to the v0.2.8 hypothesis_claim phases.
  //
  // **Conflict resolution within v0.2.13:** rules apply in registry
  // order; for any node carrying BOTH `properties.stage` and
  // `lifecycle_status`, rule #1 lifts first (writes `status` from
  // `stage`), then rule #2 renames lifecycle_status onto `status`,
  // overwriting. `lifecycle_status` wins on conflict because it was
  // the more explicit field at write time (top-level slot >
  // properties-bag slot in pre-v0.2 authoring practice). Production
  // graphs typically carry only one of the two; the conflict path is
  // theoretical but documented for predictability.
  '0.2.13': [
    // 1. (RETIRED in 0.9.10, batch-6 #33) The product `properties.stage` →
    //    top-level `status` lift is superseded by UPG-654/661, which
    //    re-canonicalised a product node's lifecycle stage to
    //    `properties.stage` — a distinct 9-phase `UPGProductStage` axis,
    //    separate from the generic top-level `status` other entities use. The
    //    entire runtime (create_product, get_graph_digest, get_product_context,
    //    completeness, header-sync) reads and writes `properties.stage`, so
    //    lifting it to `status` made a freshly-created product read as
    //    `property_drift` against its own validator (a tool emitting drift its
    //    validator immediately flags). Legacy "idea"-style values are still
    //    normalised on READ by `coerceProductStage`, so retiring the lift loses
    //    no cleanup. Decision: 2026-06-10-product-stage-properties-canonical.md.
    // 2. Rename top-level lifecycle_status → status (with value remap).
    {
      kind: 'rename_top_level',
      type: 'product',
      from: 'lifecycle_status',
      to: 'status',
      value_map: {
        // Pre-canonical lifecycle_status values from early v0.1 product-node
        // shape. Mapped to the closest UPGProductStage equivalent.
        draft: 'concept',
        active: 'launch',
        // archived/retired-style values map to sunset; explicit when seen.
        archived: 'sunset',
        retired: 'sunset',
      },
      reason: '`lifecycle_status` was a pre-canonical top-level field; `UPGBaseNode.status` is the canonical lifecycle slot. Values remapped to UPGProductStage phases.',
    },
    // 3. Drop self-referential source_id / source_type (universal cleanup).
    {
      kind: 'drop_when_self_referential',
      type: '*',
      fields: ['source_id', 'source_type'],
      reason: '`source_id` / `source_type` are round-trip metadata for entities imported from external systems (Notion, Linear). When they equal the node\'s own id/type, they\'re redundant self-references; drop with no information loss.',
    },
    // 4. Lift hypothesis_claim properties.status → top-level status with
    //    cross-lifecycle value remap. Pre-Wave-3
    //    hypothesis nodes carried lifecycle phase in `properties.status`
    //    using the old `untested → testing → resolved` enum. The Wave 3
    //    `migrate_type(hypothesis → hypothesis_claim)` pass renamed the
    //    entity type but left the property bag untouched; legacy
    //    phase values now sit in the wrong slot AND need remapping to
    //    the new hypothesis_claim lifecycle. Value map mirrors the
    //    UPG_SPLIT_MIGRATIONS['0.2.8'] routing table for consistency
    //    (untested→drafted, testing→active, resolved→active fallback,
    //    validated/invalidated identity).
    //
    //    NOTE: type stays 'hypothesis_claim' (not 'hypothesis') because
    //    nodes are typed as hypothesis_claim at v0.2.13 time. The v0.4.0
    //    entity migration later renames hypothesis_claim → hypothesis;
    //    by then the property lift has already been applied.
    {
      kind: 'lift_property_to_top_level',
      type: 'hypothesis_claim',
      from_property: 'status',
      to: 'status',
      value_map: {
        // Legacy hypothesis lifecycle (pre-v0.2.8) → new hypothesis_claim
        // lifecycle (`drafted → active → validated | invalidated | archived`).
        untested: 'drafted',
        testing: 'active',
        // `resolved` was the legacy terminal phase; without an explicit
        // core_state we treat it as `active` (pending the next refinement)
        // matching the UPG_SPLIT_MIGRATIONS routing.
        resolved: 'active',
        // Already-canonical phase values pass through unchanged via the
        // value_map identity entries (explicit so the doctrine is auditable
        // and partial maps don't surprise readers).
        drafted: 'drafted',
        active: 'active',
        validated: 'validated',
        invalidated: 'invalidated',
        archived: 'archived',
      },
      reason: 'pre-Wave-3 hypothesis nodes carried lifecycle phase inside `properties.status` using the legacy `untested → testing → resolved` enum. Wave 3 migrate_type(hypothesis → hypothesis_claim) renamed the entity but left the property untouched. This rule lifts the value to top-level `status` and remaps to the canonical hypothesis_claim lifecycle (drafted | active | validated | invalidated | archived), mirroring UPG_SPLIT_MIGRATIONS["0.2.8"] routing: untested→drafted, testing→active, resolved→active.',
    },
  ],

  // ── v0.2.14: widen `rename_top_level` to cover `outcome` ──────
  //
  // After running `migrate_properties` against unified-product-graph.upg
  // post-v0.2.13, 167 `outcome` nodes remained with a top-level
  // `lifecycle_status` field. The v0.2.13 `rename_top_level` rule was
  // scoped to `type: 'product'` only. The existing DSL stores `type` as a
  // plain string (no array variant), so a parallel rule for `outcome` is the
  // minimum-viable fix; no engine or type changes required.
  '0.2.14': [
    {
      kind: 'rename_top_level',
      type: 'outcome',
      from: 'lifecycle_status',
      to: 'status',
      value_map: {
        // Same value map as the v0.2.13 `product` rule; pre-canonical
        // lifecycle_status values from the v0.1 era.
        draft: 'concept',
        active: 'launch',
        archived: 'sunset',
        retired: 'sunset',
      },
      reason: '`outcome` nodes carried the same pre-canonical `lifecycle_status` top-level field as `product` (v0.1 era). Widening to `outcome` closes the final 167 top_level_drift rows in unified-product-graph.upg. Parallel rule used because the DSL `type` field is a plain string; minimum-viable fix with no engine change.',
    },
  ],
}

/**
 * A single property-migration change applied during `migrateNodeProperties`.
 * Surfaced for one-warning-per-file logging and structured load-time reports.
 */
export type UPGPropertyMigrationChange =
  | { kind: 'dropped'; key: string }
  | { kind: 'renamed_top_level'; from: string; to: string; value_changed: boolean }
  | { kind: 'lifted_to_top_level'; from_property: string; to: string; value_changed: boolean }
  | { kind: 'self_ref_dropped'; field: string }
  | { kind: 'remapped_property_value'; property: string; to_property?: string; value_changed: boolean }
  | { kind: 'reshaped_to_assessment'; property: string; scale_id: string; value: number; label: string }

/**
 * Apply property migrations to a single node. Returns the (possibly new) node
 * along with a structured list of changes so callers can emit warnings,
 * generate audit reports, or skip nodes that don't need rewrites.
 *
 * Operates on top-level `UPGBaseNode` fields (`status`, `lifecycle_status`,
 * `source_id`, `source_type`, …) AND on intra-`properties` keys, dispatching
 * by rule `kind`. The four kinds are orthogonal; rules apply in registry
 * order within a version, and across versions in `(fromVersion, toVersion]`.
 *
 * Backward-compat helper: callers that only care about dropped property keys
 * can derive the old `dropped: string[]` shape via
 * `changes.filter(c => c.kind === 'dropped').map(c => c.key)`.
 *
 * @example
 * // drop_props (unchanged behaviour):
 * const m = { type: 'metric', properties: { quality_score: 4, current_value: 100 } }
 * const { node, changes } = migrateNodeProperties(m, '0.2.0', '0.2.2')
 * // node.properties        === { current_value: 100 }
 * // changes[0].kind        === 'dropped'; changes[0].key === 'quality_score'
 *
 * @example
 * // lift_property_to_top_level:
 * const p = { id: 'p1', type: 'product', properties: { stage: 'idea' } }
 * const { node } = migrateNodeProperties(p, '0.2.12', '0.2.13')
 * // node.status               === 'concept'  (lifted + remapped)
 * // node.properties.stage     === undefined  (removed from properties)
 *
 * @example
 * // drop_when_self_referential (wildcard type):
 * const x = { id: 'x1', type: 'product', source_id: 'x1', source_type: 'product' }
 * const { changes } = migrateNodeProperties(x, '0.2.12', '0.2.13')
 * // changes contains { kind: 'self_ref_dropped', field: 'source_id' }
 * // changes contains { kind: 'self_ref_dropped', field: 'source_type' }
 */
export function migrateNodeProperties<
  T extends {
    id?: string
    type: string
    status?: unknown
    properties?: Record<string, unknown>
    [key: string]: unknown
  },
>(
  node: T,
  fromVersion: string,
  toVersion: string,
): { node: T; changes: UPGPropertyMigrationChange[] } {
  const changes: UPGPropertyMigrationChange[] = []
  // Mutable working copy; only realised back into the result when changes occur.
  let workingNode: Record<string, unknown> = { ...node }
  let mutated = false

  for (const [version, migrations] of Object.entries(UPG_PROPERTY_MIGRATIONS)) {
    if (!versionInRange(version, fromVersion, toVersion)) continue
    for (const m of migrations) {
      if (m.type !== '*' && m.type !== node.type) continue

      switch (m.kind) {
        case 'drop_props': {
          const props = workingNode.properties as Record<string, unknown> | undefined
          if (!props) break
          const next: Record<string, unknown> = {}
          let dirty = false
          for (const [k, v] of Object.entries(props)) {
            if (m.drop_props.includes(k)) {
              changes.push({ kind: 'dropped', key: k })
              dirty = true
            } else {
              next[k] = v
            }
          }
          if (dirty) {
            workingNode.properties = next
            mutated = true
          }
          break
        }

        case 'rename_top_level': {
          if (!(m.from in workingNode)) break
          const oldValue = workingNode[m.from]
          let newValue = oldValue
          if (m.value_map && typeof oldValue === 'string' && oldValue in m.value_map) {
            newValue = m.value_map[oldValue]
          }
          // value_changed reflects actual value mutation, not whether the map
          // had an entry for the input. Identity entries (`'drafted' → 'drafted'`)
          // surface as value_changed: false; the structural change (top-level
          // rename) is signalled by the change kind itself.
          const valueChanged = newValue !== oldValue
          workingNode[m.to] = newValue
          delete workingNode[m.from]
          mutated = true
          changes.push({ kind: 'renamed_top_level', from: m.from, to: m.to, value_changed: valueChanged })
          break
        }

        case 'lift_property_to_top_level': {
          const props = workingNode.properties as Record<string, unknown> | undefined
          if (!props || !(m.from_property in props)) break
          const oldValue = props[m.from_property]
          let newValue = oldValue
          if (m.value_map && typeof oldValue === 'string' && oldValue in m.value_map) {
            newValue = m.value_map[oldValue]
          }
          // See `rename_top_level` above: value_changed reflects mutation,
          // not map-presence. Identity entries surface as false.
          const valueChanged = newValue !== oldValue
          workingNode[m.to] = newValue
          // Remove the property from the inner bag.
          const nextProps: Record<string, unknown> = {}
          for (const [k, v] of Object.entries(props)) {
            if (k !== m.from_property) nextProps[k] = v
          }
          workingNode.properties = nextProps
          mutated = true
          changes.push({ kind: 'lifted_to_top_level', from_property: m.from_property, to: m.to, value_changed: valueChanged })
          break
        }

        case 'drop_when_self_referential': {
          for (const field of m.fields) {
            if (!(field in workingNode)) continue
            const value = workingNode[field]
            // Heuristic: *_id matches the node's id; *_type matches the node's type.
            const isSelfRef =
              (field.endsWith('_id') && typeof value === 'string' && value === node.id) ||
              (field.endsWith('_type') && typeof value === 'string' && value === node.type)
            if (isSelfRef) {
              delete workingNode[field]
              mutated = true
              changes.push({ kind: 'self_ref_dropped', field })
            }
          }
          break
        }

        case 'remap_property_value': {
          const props = workingNode.properties as Record<string, unknown> | undefined
          if (!props || !(m.property in props)) break
          const oldValue = props[m.property]
          if (typeof oldValue !== 'string' || !(oldValue in m.value_map)) break
          const mapped = m.value_map[oldValue]
          const nextProps: Record<string, unknown> = { ...props }
          if (m.to_property) {
            // Split: move the mapped value to a sibling property, reset the original.
            nextProps[m.to_property] = mapped
            nextProps[m.property] = m.reset_value ?? mapped
            changes.push({ kind: 'remapped_property_value', property: m.property, to_property: m.to_property, value_changed: true })
          } else {
            nextProps[m.property] = mapped
            changes.push({ kind: 'remapped_property_value', property: m.property, value_changed: mapped !== oldValue })
          }
          workingNode.properties = nextProps
          mutated = true
          break
        }

        case 'reshape_value_to_assessment': {
          const props = workingNode.properties as Record<string, unknown> | undefined
          if (!props || !(m.property in props)) break
          const oldValue = props[m.property]
          // Idempotent: an existing assessment object (or any non-numeric value)
          // passes through untouched. Accept a bare number or a numeric string.
          let num: number | null = null
          if (typeof oldValue === 'number' && Number.isFinite(oldValue)) num = oldValue
          else if (typeof oldValue === 'string' && oldValue.trim() !== '' && Number.isFinite(Number(oldValue))) {
            num = Number(oldValue)
          }
          if (num === null) break
          const label = getScale(m.scale_id)?.points.find((p) => p.value === num)?.label ?? String(num)
          const nextProps: Record<string, unknown> = {
            ...props,
            [m.property]: { value: num, label, scale_id: m.scale_id },
          }
          workingNode.properties = nextProps
          mutated = true
          changes.push({ kind: 'reshaped_to_assessment', property: m.property, scale_id: m.scale_id, value: num, label })
          break
        }
      }
    }
  }

  if (!mutated) return { node, changes }
  return { node: workingNode as T, changes }
}

/**
 * Returns every property migration entry between two versions; useful for
 * load-time warning generation and audit reports.
 */
export function getPropertyMigrations(
  fromVersion: string,
  toVersion: string,
): UPGPropertyMigration[] {
  const result: UPGPropertyMigration[] = []
  for (const [version, migrations] of Object.entries(UPG_PROPERTY_MIGRATIONS)) {
    if (versionInRange(version, fromVersion, toVersion)) {
      result.push(...migrations)
    }
  }
  return result
}

// ─── 1→N split migrations ───────────────────────────────────
//
// When an entity type is **decomposed** into multiple canonical types (not
// renamed (1→1) and not just losing properties (UPG_PROPERTY_MIGRATIONS) but
// genuinely split into N entities linked by canonical edges), `UPGSplitMigration`
// records the rule. First use: experiment → experiment_plan + experiment_run.
//
// **Rule shape: status-routed split (the only `kind` defined today):**
//
// ```
// {
//   kind: 'status_routed',
//   from: 'experiment',
//   status_property: 'status',                  // property to read on source
//   produces: [
//     { ref: 'plan', type: 'experiment_plan', keep_props: [...], defaults: {...} },
//     { ref: 'run',  type: 'experiment_run',  keep_props: [...], defaults: {...} },
//   ],
//   routing: {
//     'draft':     { spawn: ['plan'],          plan: { defaults: { status: 'drafted' } } },
//     'planned':   { spawn: ['plan'],          plan: { defaults: { status: 'scheduled' } } },
//     'running':   { spawn: ['plan', 'run'],   plan: { defaults: { status: 'approved' } }, run: { defaults: { status: 'in_progress' } } },
//     'analysing': { spawn: ['plan', 'run'],   plan: { defaults: { status: 'approved' } }, run: { defaults: { status: 'in_progress' } } },
//     'done':      { spawn: ['plan', 'run'],   plan: { defaults: { status: 'approved' } }, run: { defaults: { status: 'complete' } } },
//     'cancelled': { spawn: ['plan'],          plan: { defaults: { status: 'cancelled' } } },
//     'aborted':   { spawn: ['plan', 'run'],   plan: { defaults: { status: 'approved' } }, run: { defaults: { status: 'aborted' } } },
//   },
//   edges: [
//     { source_ref: 'plan', target_ref: 'run', type: 'experiment_plan_ran_as_experiment_run', when: 'both_spawned' },
//   ],
//   reason: '...',
// }
// ```
//
// **Consumer contract.** A loader/migration tool reads the rule, looks up
// the source node's `status_property` value, finds the matching `routing`
// entry, and emits one new node per `spawn` ref + the listed `edges` whose
// `when` condition is satisfied. Each spawned node:
//
// 1. Inherits id strategy: `keep_props` named as `'__id'` preserves source
//    id; otherwise a new uuid is generated. The first spawned ref keeps the
//    source id by default (so legacy references survive).
// 2. Inherits `keep_props` from source.properties (typed-string keys to
//    copy).
// 3. Receives `defaults` merged after the keep, so route-specific defaults
//    win over source values when the field is route-determined (e.g. status).
//
// **Why it lives in spec, not in the MCP server.** The rule is a contract,
// not a runtime. Putting the rule data in `@unified-product-graph/core` lets every consumer
// (mcp-server's `migrate_type` tool, the LSP, adapters, the CLI) execute
// the same translation. Runtime consumers implement the engine that walks
// this data on the MCP side; tests in `@unified-product-graph/core` validate the rule shape.

/** A single produced target within a 1→N split. */
export interface UPGSplitTarget {
  /** Local name (referenced by routing + edges within this rule). */
  ref: string
  /** Canonical entity type to spawn. */
  type: string
  /** Property keys copied from source.properties. Use `'__id'` to inherit source id. */
  keep_props?: readonly string[]
  /** Defaults merged after keep_props (route-specific defaults win). */
  defaults?: Record<string, unknown>
}

/** Per-target route plan: what to spawn and what overrides apply. */
export interface UPGSplitRouteTarget {
  /** Route-specific defaults merged on top of UPGSplitTarget.defaults. */
  defaults?: Record<string, unknown>
}

/** A single routing rule keyed by source status value. */
export interface UPGSplitRoute {
  /** Which target refs to spawn for this status. */
  spawn: readonly string[]
  /**
   * Per-target route overrides. Keys must match `produces[].ref` for
   * targets in `spawn`.
   */
  [targetRef: string]: UPGSplitRouteTarget | readonly string[] | undefined
}

/** A canonical edge to emit between spawned targets, gated by a condition. */
export interface UPGSplitEdge {
  /** Source target ref (from `produces`). */
  source_ref: string
  /** Target target ref (from `produces`). */
  target_ref: string
  /** Edge type key (must exist in UPG_EDGE_CATALOG). */
  type: string
  /**
   * When to emit the edge.
   *
   * - `'both_spawned'`: only emit if both source_ref and target_ref were
   *   spawned by the routing rule.
   * - `'always'`: emit unconditionally (rare; only valid if both refs
   *   appear in every routing entry's `spawn`).
   */
  when: 'both_spawned' | 'always'
}

/** A status-routed 1→N split migration rule. */
export interface UPGSplitMigration {
  /** Discriminator. Only one kind today. */
  kind: 'status_routed'
  /** The deprecated source type being decomposed. */
  from: string
  /** Property name on source.properties whose value drives routing. */
  status_property: string
  /** The N canonical types this rule produces. */
  produces: readonly UPGSplitTarget[]
  /** Per-status routing: keys are values of source[status_property]. */
  routing: Record<string, UPGSplitRoute>
  /** Edges to emit between spawned targets. */
  edges: readonly UPGSplitEdge[]
  /** Human-readable explanation surfaced in load-time warnings. */
  reason: string
}

/**
 * Version-scoped 1→N split migrations. Same convention as `UPG_MIGRATIONS`:
 * the key is the version that introduces the migration.
 */
export const UPG_SPLIT_MIGRATIONS: Record<string, UPGSplitMigration[]> = {
  '0.2.6': [
    // (split 1) experiment → experiment_plan + experiment_run.
    {
      kind: 'status_routed',
      from: 'experiment',
      status_property: 'status',
      produces: [
        {
          ref: 'plan',
          type: 'experiment_plan',
          keep_props: [
            // Plan-shape fields from the legacy experiment.
            '__id', 'method', 'sample_size', 'expected_lift', 'expected_lift_unit',
          ],
          defaults: {},
        },
        {
          ref: 'run',
          type: 'experiment_run',
          keep_props: [
            // Run-shape fields from the legacy experiment.
            'actual_lift', 'start_date', 'end_date',
          ],
          defaults: {},
        },
      ],
      routing: {
        // Pre-run statuses → plan only.
        draft:     { spawn: ['plan'],        plan: { defaults: { status: 'drafted' } } },
        planned:   { spawn: ['plan'],        plan: { defaults: { status: 'scheduled' } } },
        cancelled: { spawn: ['plan'],        plan: { defaults: { status: 'cancelled' } } },
        // In-flight statuses → plan + run, plan settles to approved.
        running:   { spawn: ['plan', 'run'], plan: { defaults: { status: 'approved' } }, run: { defaults: { status: 'in_progress' } } },
        analysing: { spawn: ['plan', 'run'], plan: { defaults: { status: 'approved' } }, run: { defaults: { status: 'in_progress' } } },
        // Terminal statuses → plan (approved) + run (terminal).
        done:      { spawn: ['plan', 'run'], plan: { defaults: { status: 'approved' } }, run: { defaults: { status: 'complete' } } },
        aborted:   { spawn: ['plan', 'run'], plan: { defaults: { status: 'approved' } }, run: { defaults: { status: 'aborted' } } },
      },
      edges: [
        {
          source_ref: 'plan',
          target_ref: 'run',
          type: 'experiment_plan_ran_as_experiment_run',
          when: 'both_spawned',
        },
      ],
      reason:
        'The legacy `experiment` type bundled plan-shape (method, projected reach, success criteria) with run-shape (actual lift, outcome, disposition). The seven status values divided into pre-run (draft/planned/cancelled, plan only), in-flight (running/analysing, plan + run), and terminal (done/aborted, plan + run with run terminal). The plan id keeps the source id so legacy references survive; the run gets a fresh id.',
    },
  ],

  '0.2.7': [
    // (split 2) user_story → story_statement + story_task.
    //
    // Always-spawn-both shape (no status routing; every user_story
    // produces exactly one statement + one task linked by `implements`).
    // The statement carries the "as-a / i-want / so-that" templated
    // promise (lifecycle-free); the task carries the lifecycle and
    // estimation/assignment fields.
    //
    // Every legacy user_story.status value maps to a story_task.status
    // in the WORK_ITEM template; the statement remains lifecycle-free.
    //
    //   user_story.status  → story_task.status
    //   draft              → todo
    //   ready              → todo (acceptance criteria refined; not yet started)
    //   in_progress        → in_progress
    //   done               → done
    {
      kind: 'status_routed',
      from: 'user_story',
      status_property: 'status',
      produces: [
        {
          ref: 'task',
          type: 'task',
          keep_props: [
            // Task-shape fields from the legacy user_story.
            '__id', 'estimate', 'effort', 'priority',
          ],
          defaults: {},
        },
        {
          ref: 'statement',
          type: 'story_statement',
          keep_props: [
            // Statement-shape fields (the templated promise).
            'as_a', 'i_want_to', 'so_that', 'text',
          ],
          defaults: {},
        },
      ],
      routing: {
        // Every legacy status spawns both. The statement is lifecycle-free
        // (no status); only the task takes a status.
        draft:       { spawn: ['task', 'statement'], task: { defaults: { status: 'todo' } } },
        ready:       { spawn: ['task', 'statement'], task: { defaults: { status: 'todo' } } },
        in_progress: { spawn: ['task', 'statement'], task: { defaults: { status: 'in_progress' } } },
        done:        { spawn: ['task', 'statement'], task: { defaults: { status: 'done' } } },
      },
      edges: [
        {
          source_ref: 'task',
          target_ref: 'statement',
          type: 'task_implements_story_statement',
          when: 'both_spawned',
        },
      ],
      reason:
        'The legacy `user_story` type bundled the templated "As X, I want Y so Z" promise (a stable design artefact) with the engineering work to deliver it (a lifecycle-bearing task). Every legacy row produces 1 statement + 1 task linked by `implements`. The task id keeps the source id (legacy references survive); the statement gets a derived id. Statement is lifecycle-free; task uses the WORK_ITEM template.',
    },
  ],

  '0.2.8': [
    // (split 3) hypothesis → hypothesis_claim + hypothesis_evidence.
    //
    // **Always-spawn-claim, never-spawn-evidence-from-legacy** shape.
    // Legacy hypothesis rows only carried the belief properties
    // (we_believe / will_result_in / we_know_when / we_test_by); they
    // never carried inline evidence; evidence was always external,
    // attached via the dropped `evidence_supports_hypothesis` edge from
    // `evidence` rows. So the migration is conceptually a 1→1 rename plus
    // a property cleanup (drop we_test_by, which is about experimental
    // method, which now lives on experiment_plan via
    // `hypothesis_claim_requires_experiment_plan`).
    //
    // The split rule is registered in UPG_SPLIT_MIGRATIONS for symmetry
    // with the other splits and to allow future tools that want to spawn
    // evidence from richer legacy data shapes; the inline applySplit()
    // runner produces a single claim by default.
    //
    // The legacy `untested → testing → resolved (validated|invalidated)`
    // status enum maps to claim lifecycle: untested → drafted, testing →
    // active, resolved → validated|invalidated based on the resolved
    // core_state. archived has no legacy equivalent (claim-specific).
    {
      kind: 'status_routed',
      from: 'hypothesis',
      status_property: 'status',
      produces: [
        {
          ref: 'claim',
          type: 'hypothesis',
          keep_props: [
            // Statement-shape fields preserved byte-for-byte.
            '__id', 'we_believe', 'will_result_in', 'we_know_when',
          ],
          defaults: {},
        },
      ],
      routing: {
        untested:    { spawn: ['claim'], claim: { defaults: { status: 'drafted' } } },
        testing:     { spawn: ['claim'], claim: { defaults: { status: 'active' } } },
        // legacy `resolved` had two core_states (validated/invalidated); the
        // routing key matches the resolved core_state directly when present
        // (loaders emit those as the status value).
        validated:   { spawn: ['claim'], claim: { defaults: { status: 'validated' } } },
        invalidated: { spawn: ['claim'], claim: { defaults: { status: 'invalidated' } } },
        // Catch-all for `resolved` without a core_state; treat as active
        // pending the resolver call to decide validated/invalidated.
        resolved:    { spawn: ['claim'], claim: { defaults: { status: 'active' } } },
      },
      // No edges emitted; the supports/refutes/derived_from edges all
      // attach hypothesis_evidence rows that legacy hypothesis nodes did
      // NOT have. Evidence migration is consumer-driven (Entopo/MCP can
      // walk the dropped `evidence_supports_hypothesis` edges and spawn
      // hypothesis_evidence rows; but that's adapter logic, not spec
      // migration data).
      edges: [],
      reason:
        'Legacy hypothesis rows carry the belief properties; the claim preserves them byte-for-byte. The we_test_by property drops (it described experimental method, which now lives on the linked experiment_plan via hypothesis_claim_requires_experiment_plan). Evidence rows are not spawned from legacy hypothesis data; legacy hypothesis never carried inline evidence. Consumers walking dropped `evidence_supports_hypothesis` edges may opt to spawn hypothesis_evidence rows post-migration, but that\'s out-of-band adapter logic.',
    },
  ],
}

/**
 * Get all 1→N split migrations between two versions.
 *
 * @example
 * const splits = getSplitMigrations('0.2.5', '0.2.6')
 * // splits[0].from === 'experiment'
 * // splits[0].produces.length === 2
 */
export function getSplitMigrations(
  fromVersion: string,
  toVersion: string,
): UPGSplitMigration[] {
  const result: UPGSplitMigration[] = []
  for (const [version, migrations] of Object.entries(UPG_SPLIT_MIGRATIONS)) {
    if (versionInRange(version, fromVersion, toVersion)) {
      result.push(...migrations)
    }
  }
  return result
}

// ─── Scalar → edge promotions (P14 conformance) ─────────────────────
//
// When a scalar property holds the *identity of a first-class entity* — one
// you would query, aggregate, or compare across instances — principle P14
// ("Foreign Keys Are Edges") says it should be a canonical edge, not a string.
// `UPGScalarToEdgeMigration` records the lossless promotion: find-or-create the
// referenced entity by title, link it with the canonical edge, then (usually)
// drop the now-redundant scalar.
//
// **Why a new migration family (not a `UPGPropertyMigration`).** The property
// union is consumed by `migrateNodeProperties`, which is per-node and stateless
// — it cannot see sibling nodes or mint edges. Minting a target node + an edge
// is a **graph-level** operation, like `UPGSplitMigration`. So this is a sibling
// graph-level family: declared here as a versioned contract; applied by a
// graph-aware consumer (`UPGFileStore.applyScalarToEdgeMigrations`, the CLI, or
// any adapter that walks this data). Tests in `@unified-product-graph/core`
// validate the rule SHAPE (every edge/entity type referenced exists); the SDK
// owns the apply ENGINE and its round-trip tests.
//
// **Lossless (Path B), idempotent, reversible.** The scalar's value becomes (or
// links to) a real node — nothing is discarded. Re-running links/mints nothing
// new (title already indexed, edge already present). A pre-apply snapshot makes
// it reversible.

/**
 * Promote a scalar property that names a first-class entity into a canonical edge.
 * Graph-level (mints/links nodes) — NOT applied by `migrateNodeProperties`.
 */
export interface UPGScalarToEdgeMigration {
  /** Source entity type carrying the scalar. */
  from_type: string
  /** Property key holding the entity identity (inside `properties`, or top-level if `top_level`). */
  scalar_property: string
  /** Whether the scalar lives at top-level instead of inside `properties`. */
  top_level?: boolean
  /** Canonical target entity type to find-or-create. */
  target_type: string
  /** Canonical edge to create. Must exist in `UPG_EDGE_CATALOG`. */
  edge_type: string
  /**
   * Edge orientation. Default `false`: the `from_type` node is the edge SOURCE
   * (`from → target`). `true`: the resolved target entity is the edge source
   * (`target → from`) — use when an EXISTING edge's canonical direction runs
   * target_type → from_type (e.g. `acquisition_channel_runs_growth_campaign`,
   * whose scalar lives on `growth_campaign`).
   */
  reverse?: boolean
  /** How to resolve an existing target before minting: normalized title (default) or exact id. */
  target_match?: 'title' | 'id'
  /** Default properties merged onto a freshly-minted target (e.g. `{ designation: 'north_star' }`). */
  target_defaults?: Record<string, unknown>
  /** `string[]` scalars (e.g. `success_metrics`) → one edge per element. */
  multi?: boolean
  /** Drop the scalar after linking. `true` for orphans/shadows; `false` to keep an actor display-cache. */
  drop_scalar: boolean
  /** Human-readable explanation surfaced in load-time warnings + changelog. */
  reason: string
}

/**
 * Version-scoped scalar→edge promotions. Same convention as `UPG_MIGRATIONS` /
 * `UPG_SPLIT_MIGRATIONS`: the key is the version that introduces the rule.
 */
export const UPG_SCALAR_TO_EDGE_MIGRATIONS: Record<string, UPGScalarToEdgeMigration[]> = {
  '0.32.0': [
    // C2 — assignment gets its own edge, superseding the 0.12.0 rules that
    // routed task/bug `assignee` into node_owned_by_person.
    //
    // WHY THIS IS A REVERSAL AND NOT A GAP-FILL. 0.12.0 ruled that an assignee
    // is an owner. Assignment has a time interval and an exclusivity that
    // ownership does not, and a real board ran 85% unassigned while every item
    // was owned — a state one edge cannot express.
    //
    // WHY EXISTING EDGES ARE NOT REWRITTEN. A node_owned_by_person edge on a
    // task is indistinguishable by inspection from a genuine ownership edge, so
    // a blanket rename would silently reclassify real facts. Measured population
    // across every graph in the originating workspace was ~zero, which makes the
    // cost of leaving them nil and the cost of guessing a wrong fact.
    //
    // user_story is included here and was MISSING from 0.12.0 entirely, though
    // it has carried `assignee` since 0.20.0.
    { from_type: 'task', scalar_property: 'assignee', target_type: 'person', edge_type: 'node_assigned_to_person', drop_scalar: false,
      reason: 'P14 actor: the assignee is a first-class person ("every task assigned to this person"). Assignment is not ownership: it has an interval and an exclusivity that ownership lacks, so it links node_assigned_to_person, superseding the 0.12.0 node_owned_by_person rule. Display string kept.' },
    { from_type: 'bug', scalar_property: 'assignee', target_type: 'person', edge_type: 'node_assigned_to_person', drop_scalar: false,
      reason: 'P14 actor: same as task. Supersedes the 0.12.0 node_owned_by_person rule. Display string kept.' },
    { from_type: 'user_story', scalar_property: 'assignee', target_type: 'person', edge_type: 'node_assigned_to_person', drop_scalar: false,
      reason: 'P14 actor: user_story has carried `assignee` since 0.20.0 and had no promotion rule at all; an asymmetry with task and bug that this closes. Display string kept.' },
  ],
  '0.12.0': [
    // ── Bucket A1 — orphan scalars (no edge existed): add edge + migrate + drop ──
    {
      from_type: 'business_model', scalar_property: 'north_star_metric',
      target_type: 'metric', edge_type: 'business_model_guided_by_metric',
      target_match: 'title', target_defaults: { designation: 'north_star' },
      drop_scalar: true,
      reason:
        'P14: the optimised metric is a first-class entity you query across business models ("which models optimise for this metric"), not a label. Mint metric{designation:north_star} + link via business_model_guided_by_metric; drop the orphan string.',
    },
    // NOTE: revenue_stream.forecast is NOT promoted — its JSDoc ("Free-text
    // forecast or projection") is narrative prose, not a ref to a forecast
    // entity. The additive revenue_stream_projected_by_forecast edge exists for
    // an explicit stream→forecast link; the free-text field stays.
    { from_type: 'metric', scalar_property: 'data_source', target_type: 'data_source', edge_type: 'metric_fed_by_data_source', drop_scalar: true,
      reason: 'P14: the data source feeding a metric is queryable across metrics ("everything fed by this source"); promote the string to a metric_fed_by_data_source edge.' },
    { from_type: 'hallucination_report', scalar_property: 'root_cause', target_type: 'root_cause', edge_type: 'hallucination_report_has_root_cause', drop_scalar: true,
      reason: 'P14: a root_cause is a first-class entity reused across reports; link it rather than restating the cause as a string.' },
    // NOTE: prompt_version.template is NOT promoted — its JSDoc ("Template body
    // with variable placeholders") is the prompt CONTENT, not a ref to a
    // prompt_template entity. The additive prompt_version_child_of_prompt_template
    // edge exists for an explicit version→template link; the content field stays.
    { from_type: 'model_comparison', scalar_property: 'model_ids', target_type: 'ai_model', edge_type: 'model_comparison_compares_ai_model', multi: true, drop_scalar: true,
      reason: 'P14: the models being compared are first-class ai_model entities; one compares edge per model replaces the id-string list.' },
    { from_type: 'launch', scalar_property: 'success_metrics', target_type: 'metric', edge_type: 'launch_measured_by_metric', multi: true, drop_scalar: true,
      reason: 'P14: success metrics are first-class metric entities measured across launches; one measured_by edge per metric replaces the string list.' },
    // NOTE: user_story.as_a is NOT promoted — it is a load-bearing field of the
    // user_story → story_statement split (the "As a <persona>" templated promise),
    // and the value is often a role descriptor, not a persona node. Left in place.
    { from_type: 'wireframe', scalar_property: 'screen_name', target_type: 'screen', edge_type: 'wireframe_depicts_screen', drop_scalar: true,
      reason: 'P14: the depicted screen is a first-class entity; link it via wireframe_depicts_screen rather than naming it in a string.' },
    { from_type: 'service_level_indicator', scalar_property: 'metric_name', target_type: 'metric', edge_type: 'service_level_indicator_measures_metric', drop_scalar: true,
      reason: 'P14: the SLI’s metric is a first-class entity; link it via service_level_indicator_measures_metric rather than naming it in a string.' },
    { from_type: 'agent_definition', scalar_property: 'model_ref', target_type: 'ai_model', edge_type: 'agent_definition_uses_ai_model', drop_scalar: true,
      reason: 'P14: the model an agent uses is a first-class ai_model entity ("every agent on this model"); promote the ref string to a uses edge.' },
    { from_type: 'privacy_policy', scalar_property: 'regulations', target_type: 'compliance_requirement', edge_type: 'privacy_policy_governs_compliance_requirement', multi: true, drop_scalar: true,
      reason: 'P14: each regulation is a first-class compliance_requirement entity tracked across policies; one governs edge per requirement replaces the string list.' },
    { from_type: 'a11y_annotation', scalar_property: 'target_component', target_type: 'design_component', edge_type: 'a11y_annotation_targets_design_component', drop_scalar: true,
      reason: 'P14: the annotated component is a real design_component entity; link it so accessibility coverage rolls up by component.' },
    { from_type: 'metric_quality_assessment', scalar_property: 'proxy_alternatives', target_type: 'metric', edge_type: 'metric_quality_assessment_considers_proxy_metric', multi: true, drop_scalar: true,
      reason: 'P14: proxy alternatives are first-class metric entities; one considers_proxy edge per metric replaces the name list.' },
    { from_type: 'ai_experiment', scalar_property: 'foundation_model', target_type: 'ai_model', edge_type: 'ai_experiment_based_on_ai_model', drop_scalar: true,
      reason: 'P14: the foundation model is a first-class ai_model entity reused across experiments; promote the string to a based_on edge.' },
    { from_type: 'rebuttal', scalar_property: 'evidence_refs', target_type: 'evidence', edge_type: 'rebuttal_supported_by_evidence', multi: true, drop_scalar: true,
      reason: 'P14: each evidence ref is a first-class evidence entity; one supported_by edge per item replaces the ref-string list.' },
    { from_type: 'bug', scalar_property: 'affected_version', target_type: 'release', edge_type: 'bug_observed_in_release', drop_scalar: true,
      reason: 'P14: the affected version is a first-class release entity ("every bug in this release"); promote the version string to an observed_in edge.' },
    { from_type: 'vulnerability', scalar_property: 'affected_package', target_type: 'library_dependency', edge_type: 'vulnerability_affects_library_dependency', drop_scalar: true,
      reason: 'P14: the affected package is a first-class library_dependency entity; link it so vulnerabilities roll up by dependency.' },
    { from_type: 'a11y_issue', scalar_property: 'discovered_in', target_type: 'screen', edge_type: 'a11y_issue_found_in_screen', drop_scalar: true,
      reason: 'P14: the screen an issue was found in is a real screen entity; link it so issues roll up by screen rather than naming it in a string.' },
    { from_type: 'team_okr', scalar_property: 'cascade_from', target_type: 'team_okr', edge_type: 'team_okr_cascades_from_team_okr', drop_scalar: true,
      reason: 'P14: a cascaded OKR points at its parent OKR (a real entity); the cascade is a self-referential edge, not a name string.' },
    { from_type: 'agent_session', scalar_property: 'tools_invoked', target_type: 'agent_skill', edge_type: 'agent_session_invoked_agent_skill', multi: true, drop_scalar: true,
      reason: 'P14: invoked tools are first-class agent_skill entities tracked across sessions; one invoked edge per skill replaces the string list.' },
    { from_type: 'agent_task', scalar_property: 'tools', target_type: 'agent_skill', edge_type: 'agent_task_uses_agent_skill', multi: true, drop_scalar: true,
      reason: 'P14: the tools a task uses are first-class agent_skill entities; one uses edge per skill replaces the string list.' },
    { from_type: 'participant', scalar_property: 'segment', target_type: 'behavioral_segment', edge_type: 'participant_belongs_to_behavioral_segment', drop_scalar: true,
      reason: 'P14: a participant’s segment is a first-class behavioral_segment entity queried across studies; promote the string to a belongs_to edge.' },
    { from_type: 'classification_axis', scalar_property: 'owner_product', target_type: 'product', edge_type: 'classification_axis_owned_by_product', target_match: 'id', drop_scalar: true,
      reason: 'P14: the product that owns an axis is a first-class entity; link it via classification_axis_owned_by_product. The scalar holds a product id, so match by id (no mint).' },

    // ── Bucket A2 — shadow scalars (the canonical edge ALREADY exists): backfill + drop ──
    { from_type: 'growth_campaign', scalar_property: 'channels_targeted', target_type: 'acquisition_channel', edge_type: 'acquisition_channel_runs_growth_campaign', reverse: true, multi: true, drop_scalar: true,
      reason: 'P14 shadow: the acquisition_channel_runs_growth_campaign edge already exists (channel→campaign). Backfill any string-only channels as edges, then drop the drift-prone list.' },
    { from_type: 'journey_step', scalar_property: 'touchpoint', target_type: 'touchpoint', edge_type: 'touchpoint_occurs_in_journey_step', reverse: true, drop_scalar: true,
      reason: 'P14 shadow: the touchpoint_occurs_in_journey_step edge already exists (touchpoint→journey_step) and the field was @deprecated 0.9.9. Backfill + finish the drop.' },
    { from_type: 'decision', scalar_property: 'superseded_by', target_type: 'decision', edge_type: 'decision_superseded_by_decision', drop_scalar: true,
      reason: 'P14 shadow: the decision_superseded_by_decision edge already exists. Backfill the superseding decision as an edge, then drop the reference string.' },
    { from_type: 'subscription', scalar_property: 'plan_name', target_type: 'pricing_tier', edge_type: 'subscription_subscribes_to_pricing_tier', drop_scalar: true,
      reason: 'P14 shadow: the subscription_subscribes_to_pricing_tier edge already exists. Backfill the plan as a pricing_tier edge, then drop the name string.' },
  ],
  '0.12.4': [
    // ── Bucket B — actor-as-string (ADDITIVE: add the ownership edge, KEEP the display string) ──
    // Per the scalar-vs-edge ADR display-cache rule (2026-06-16) and T3.1 (reuse the
    // polymorphic node_owned_by_* family; do NOT mint typed actor edges). `drop_scalar`
    // is FALSE for every entry: the string stays as a human-readable display-cache, and
    // the edge makes "everything owned by X" queryable across instances. OPT-IN — consumers
    // run promote_scalars_to_edges when they want the edges; nothing is forced on upgrade.
    // person-vs-team is the field-level call (ratified 2026-06-16): org units + durable
    // platform/components → team (9); work items, authored artifacts, and DRIs → person.
    // Two single-string participant fields (threat_model.participants, ceremony.participants)
    // are intentionally NOT here: a free-text name list cannot be promoted to one edge
    // without guessing — they are re-doc'd as display-cache only.

    // ── owned by a team (org units + durable platform/components) ──
    { from_type: 'feature_area', scalar_property: 'owner', target_type: 'team', edge_type: 'node_owned_by_team', drop_scalar: false,
      reason: 'P14 actor: a feature area is owned by a team; link node_owned_by_team so "everything this team owns" is queryable. Display string kept as a cache.' },
    { from_type: 'bounded_context', scalar_property: 'team_owner', target_type: 'team', edge_type: 'node_owned_by_team', drop_scalar: false,
      reason: 'P14 actor: the owning team is a first-class entity; link node_owned_by_team. Display string kept as a cache.' },
    { from_type: 'service', scalar_property: 'owner', target_type: 'team', edge_type: 'node_owned_by_team', drop_scalar: false,
      reason: 'P14 actor: Backstage-style service ownership rolls up by team; link node_owned_by_team. Display string kept as a cache.' },
    { from_type: 'product_area', scalar_property: 'owner', target_type: 'team', edge_type: 'node_owned_by_team', drop_scalar: false,
      reason: 'P14 actor: a product area is an org unit owned by a team; link node_owned_by_team. Display string kept as a cache.' },
    { from_type: 'api_contract', scalar_property: 'owner', target_type: 'team', edge_type: 'node_owned_by_team', drop_scalar: false,
      reason: 'P14 actor: an API contract is a service-interface component owned by the team that owns the service (Backstage); link node_owned_by_team. Display string kept as a cache.' },
    { from_type: 'database_schema', scalar_property: 'owner', target_type: 'team', edge_type: 'node_owned_by_team', drop_scalar: false,
      reason: 'P14 actor: a database schema is a data component owned by the team responsible for it; link node_owned_by_team. Display string kept as a cache.' },
    { from_type: 'design_system', scalar_property: 'maintainer', target_type: 'team', edge_type: 'node_owned_by_team', drop_scalar: false,
      reason: 'P14 actor: a design system is a shared platform maintained by a team; link node_owned_by_team. Display string kept as a cache.' },
    { from_type: 'service_level_agreement', scalar_property: 'owner', target_type: 'team', edge_type: 'node_owned_by_team', drop_scalar: false,
      reason: 'P14 actor: an SLA is a service-provider-side commitment owned by a team; link node_owned_by_team. Display string kept as a cache.' },
    { from_type: 'key_activity', scalar_property: 'operational_owner', target_type: 'team', edge_type: 'node_owned_by_team', drop_scalar: false,
      reason: 'P14 actor: the operationally accountable owner of a recurring activity is a team; link node_owned_by_team. Display string kept as a cache.' },

    // ── owned by a person (DRIs, work items, authored artifacts; the string is a name, handle, or email) ──
    { from_type: 'strategic_theme', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the theme owner is a first-class person; link node_owned_by_person so cross-theme ownership is queryable. Display string kept.' },
    { from_type: 'initiative', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the initiative owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'strategic_pillar', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the pillar owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'metric', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the metric owner is a first-class person ("every metric this person owns"); link node_owned_by_person. Display string kept.' },
    { from_type: 'data_domain', scalar_property: 'steward', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the data steward is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'partnership', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the partnership owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'key_resource', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the resource owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'document', scalar_property: 'author', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the author is a first-class person ("every document by this author"); link node_owned_by_person. Display string kept.' },
    { from_type: 'outcome', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the outcome owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'feature', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the feature owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'epic', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the epic owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'release', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the release owner is a first-class person; link node_owned_by_person. Display string kept.' },
    // NOTE (0.32.0): the task/bug `assignee` rules that lived here are SUPERSEDED
    // by the '0.32.0' block below, which routes them to node_assigned_to_person.
    // They are removed rather than left to double-run: two rules over one scalar
    // would mint the person once and link it twice, under two verbs that now mean
    // different things. Graphs promoted before 0.32.0 keep the ownership edges
    // they were given — see the 0.32.0 block for why those are not rewritten.
    { from_type: 'roadmap', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the roadmap owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'journey_step', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the journey-step owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'design_concept', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the design-concept owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'technical_debt_item', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the debt-item owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'feature_flag', scalar_property: 'owner', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the flag owner is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'deployment', scalar_property: 'deployer', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the deployer is a first-class person ("every deployment by this person"); link node_owned_by_person. Display string kept.' },
    { from_type: 'investigation', scalar_property: 'lead_investigator', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the lead investigator is a first-class person; link node_owned_by_person. Display string kept.' },
    { from_type: 'department', scalar_property: 'leader', target_type: 'person', edge_type: 'node_owned_by_person', drop_scalar: false,
      reason: 'P14 actor: the department leader is a first-class person; link node_owned_by_person. Display string kept.' },

    // ── owned by people (multi: string[] of names) ──
    { from_type: 'decision', scalar_property: 'decision_makers', target_type: 'person', edge_type: 'node_owned_by_person', multi: true, drop_scalar: false,
      reason: 'P14 actor: each decision-maker is a first-class person; one node_owned_by_person edge per name. Display list kept as a cache.' },
    { from_type: 'review_gate', scalar_property: 'required_approvers', target_type: 'person', edge_type: 'node_owned_by_person', multi: true, drop_scalar: false,
      reason: 'P14 actor: each required approver is a first-class person; one node_owned_by_person edge per name. Display list kept as a cache.' },
  ],
}

/**
 * Get all scalar→edge promotions between two versions. Mirrors `getSplitMigrations`.
 *
 * @example
 * const rules = getScalarToEdgeMigrations('0.11.6', '0.12.0')
 * // rules[0].from_type === 'business_model'
 */
export function getScalarToEdgeMigrations(
  fromVersion: string,
  toVersion: string,
): UPGScalarToEdgeMigration[] {
  const result: UPGScalarToEdgeMigration[] = []
  for (const [version, migrations] of Object.entries(UPG_SCALAR_TO_EDGE_MIGRATIONS)) {
    if (versionInRange(version, fromVersion, toVersion)) {
      result.push(...migrations)
    }
  }
  return result
}

// ─── Edge type migrations ───────────────────────────────────────────
//
// When a canonical edge key is renamed or dropped, `UPG_EDGE_MIGRATIONS`
// records the rule. Mirrors the discriminated-union shape used by
// `UPG_MIGRATIONS` and `UPG_PROPERTY_MIGRATIONS`: each version key maps to
// an array of rules; each rule carries a required `reason` quoting the
// originating CHANGELOG section so load-time warnings and changelog
// generation share one canonical source.
//
// **Composition with node migration.** `UPG_EDGE_MIGRATIONS` runs *after*
// entity migration. The runtime contract is:
//
//   1. Entity migration first via `migrateNode` (1→1 aliases) and/or
//      `applySplit` (1→N rules).
//   2. Edge migration second via `migrateEdge`;
//      `requires_source_type` / `requires_target_type` guards check the
//      *post-migration* endpoint types.
//
// This is what makes the "legacy edges on already-migrated nodes" case
// safe. The 1→N split rules already emit canonical edges between spawned
// targets via `UPG_SPLIT_MIGRATIONS[].edges`; edge migration only handles
// the residue.

/**
 * A single edge-key migration rule. Discriminated by `kind`.
 *
 * `rename` retargets `from` to `to` (optionally swapping endpoints when
 * `flip` is true) and may gate on endpoint identity via
 * `requires_source_type` / `requires_target_type`.
 *
 * `drop` removes the edge entirely (no replacement key); used when a
 * legacy edge has been superseded by a structurally different canonical
 * edge whose endpoints don't match the legacy rule's `from`.
 */
export type UPGEdgeMigration =
  | {
      kind: 'rename'
      /** The old edge type key. */
      from: string
      /** The new canonical edge type key. */
      to: string
      /** When true, swap source/target on each migrated edge. */
      flip?: boolean
      /** Required source-node type (post-migration) for this rule to fire. */
      requires_source_type?: string
      /** Required target-node type (post-migration) for this rule to fire. */
      requires_target_type?: string
      /** Human-readable reason quoting the originating CHANGELOG section. */
      reason: string
    }
  | {
      kind: 'drop'
      /** The old edge type key being removed without replacement. */
      from: string
      /** Human-readable reason quoting the originating CHANGELOG section. */
      reason: string
    }

/**
 * Version-scoped edge migration registry.
 * Key is the version that INTRODUCES the migration (target version).
 */
export const UPG_EDGE_MIGRATIONS: Record<string, UPGEdgeMigration[]> = {
  '0.33.0': [
    // G1 — the project membership edge widens from epic to the work-item set.
    // Same construction as the 0.32.0 cadence rename directly below, and for the
    // same measured reason: the adapters default an unrecognised issue to `task`,
    // so the type a project most needed to deliver was the one type it could not
    // reach. No requires_target_type, because the destination's target_type is
    // the `node` wildcard and endpoint guards check the catalog's declared type
    // rather than the concrete instance. Gate only on the unwidened source.
    { kind: 'rename', from: 'project_delivers_epic', to: 'project_delivers_work_item', requires_source_type: 'project', reason: '0.33.0: a project could only deliver an epic, while a Linear dry-run carried 651 project memberships as properties.linear_project_id and emitted zero edges. Widened to the polymorphic work-item endpoint; no flip, source unchanged. Measured live population of the old type at release: one, in the CI saturation fixture, so this rename is a vocabulary change rather than a data migration.' },
  ],
  '0.32.0': [
    // C5 — the cadence scheduling edge widens from user_story to the work-item
    // set. No requires_target_type: the destination's target_type is the `node`
    // wildcard, and endpoint guards are checked against the catalog's declared
    // type rather than the concrete instance (same reasoning as the 0.22.0
    // workspace_produced_node rename below). Gate only on the unwidened source.
    { kind: 'rename', from: 'planning_cycle_schedules_user_story', to: 'planning_cycle_schedules_work_item', requires_source_type: 'planning_cycle', reason: '0.32.0: a cycle could only schedule a user_story, while tracker imports produce `task` by default; the type a cadence most needed to hold was the one it could not reach. Widened to the polymorphic work-item endpoint; no flip, source unchanged. Measured live population of the old type at release: zero (no .upg in the repo or the dogfood graph carried it), so this rename is a vocabulary change, not a data migration.' },
  ],
  // (target version TBD at release ceremony — pencilled at 0.22.0, the next
  // breaking slot after 0.21.0 per the docket; adjust the key if Wave 4
  // claims it first.) WS3 commit-provenance collapse (2026-07-05, ratified):
  // `workspace_produced_decision` widens to the `node` wildcard endpoint —
  // a workspace's commit loop can legitimately produce any entity type
  // arranged in it (decision, feature, persona, ...), not just decisions.
  // No flip (source stays workspace); the target node's concrete type is
  // unchanged (still a decision), only the edge key + catalog target_type
  // widen. See the enum-vs-polymorphism ADR and the WS3 proposal doc for
  // the full reasoning (mirrors decision_produces_node).
  '0.22.0': [
    // No requires_target_type: the destination's target_type is the `node`
    // wildcard, and the endpoint guard is checked against the catalog's
    // declared type (per the spec-integrity "endpoint guards match the
    // catalog entry" test), not the concrete instance type — a concrete
    // `decision` instance would never satisfy a literal 'node' guard at
    // runtime. Gate only on the unwidened source endpoint.
    { kind: 'rename', from: 'workspace_produced_decision', to: 'workspace_produced_node', requires_source_type: 'workspace', reason: 'WS3 commit-provenance collapse (2026-07-05, Captain-ratified). workspace_produced_decision widens to the polymorphic workspace_produced_node: a workspace commit can legitimately produce any entity type, not just a decision. No flip; only the target_type widens from decision to the node wildcard.' },
  ],
  '0.9.9': [
    // (since v0.9.9, UPG-664/UPG-678) Validation experiment-model + test_plan
    // re-home. The validation chain is reshaped to the single-parent line
    // hypothesis → experiment_plan → experiment → experiment_run, `experiment`
    // gains its stable hypothesis loop, and `test_plan` re-homes to the QA
    // domain (its planning role absorbed by `experiment_plan`).
    //
    // The plan→experiment containment inverts: the old `experiment_has_plan`
    // (experiment → experiment_plan, hierarchy) becomes
    // `experiment_plan_designs_experiment` (experiment_plan → experiment). Flip
    // swaps endpoints. Endpoint guards reference the post-flip orientation: the
    // original source (experiment) becomes the new target, the original target
    // (experiment_plan) becomes the new source.
    { kind: 'rename', from: 'experiment_has_plan', to: 'experiment_plan_designs_experiment', flip: true, requires_source_type: 'experiment', requires_target_type: 'experiment_plan', reason: 'Validation experiment-model reshape (UPG-664). The plan owns the experiment it designs; the containment direction inverts from experiment → experiment_plan to experiment_plan → experiment. Flip swaps endpoints so the surviving edge reads plan-designs-experiment.' },
    // test_plan re-homed validation → QA (UPG-678). Its two validation-side
    // edges are dropped: a hypothesis no longer plans via a QA test_plan (it
    // requires an experiment_plan), and the test_plan → experiment_run ran-as
    // bridge is superseded by the experiment_plan → experiment flow.
    { kind: 'drop', from: 'hypothesis_planned_via_test_plan', reason: 'test_plan re-homed validation → QA (UPG-678). A hypothesis no longer plans via test_plan; the validation plan is experiment_plan (hypothesis_requires_experiment_plan).' },
    { kind: 'drop', from: 'test_plan_ran_as_experiment_run', reason: 'test_plan re-homed validation → QA (UPG-678). The Strategyzer Test Card flow now runs hypothesis → experiment_plan → experiment → experiment_run; the test_plan → experiment_run bridge is superseded by experiment_plan_designs_experiment.' },

    // (since v0.9.9, UPG-665) AI prompt-model correction. The prompt abstraction
    // is inverted to the correct ai_model → prompt_template → prompt_version
    // containment. The backwards/obsolete edges are dropped, and the
    // prompt_template → ai_model "targets" relationship is flipped into the new
    // ai_model → prompt_template ownership.
    { kind: 'rename', from: 'prompt_template_targets_ai_model', to: 'ai_model_defines_prompt_template', flip: true, requires_source_type: 'prompt_template', requires_target_type: 'ai_model', reason: 'AI prompt-model correction (UPG-665). The model owns its templates; the prompt_template → ai_model "targets" edge becomes the ai_model → prompt_template ownership edge. Flip swaps endpoints so the surviving edge reads model-defines-template.' },
    { kind: 'drop', from: 'prompt_version_evolves_prompt_template', reason: 'AI prompt-model correction (UPG-665). The backwards prompt_version → prompt_template edge is retired; prompt_template now contains its versions (prompt_template_contains_prompt_version).' },
    { kind: 'drop', from: 'ai_model_prompted_via_prompt_version', reason: 'AI prompt-model correction (UPG-665). The model no longer owns prompt_version directly; the chain is ai_model → prompt_template → prompt_version.' },
    { kind: 'drop', from: 'product_prompted_via_prompt_template', reason: 'AI prompt-model correction (UPG-665). prompt_template re-parented product → ai_model (ai_model_defines_prompt_template); the product-level containment is removed.' },
    { kind: 'drop', from: 'content_calendar_schedules_prompt_template', reason: 'AI prompt-model correction (UPG-665). prompt_template re-homed into the ai_model containment tree; it is an AI artefact, not a content-calendar-scheduled item.' },

    // (since v0.9.9, UPG-676) Strategic-theme containment edge rename. The
    // malformed `objective_rolls_up_to_strategic_theme` (key read object-first
    // while source_type was strategic_theme; forward_verb was the malformed
    // `contains_objective`) is renamed to the clean source-first
    // `strategic_theme_contains_objective`. Endpoints unchanged
    // (strategic_theme → objective); only the key and forward verb change.
    { kind: 'rename', from: 'objective_rolls_up_to_strategic_theme', to: 'strategic_theme_contains_objective', requires_source_type: 'strategic_theme', requires_target_type: 'objective', reason: 'Strategic-theme containment edge rename (UPG-676). The key now reads source-first and the forward verb is the clean `contains` (was the malformed `contains_objective`). Endpoints unchanged (strategic_theme → objective); satisfies the F2 prefix gate.' },

    // (since v0.9.9, UPG-677) Duplicate / shadow edge collapse. Each retired key
    // is dual-read to the surviving canonical key. Tier 1 = byte-identical
    // shadows; Tier 2 = tense/suffix twins; Tier 3 = near-synonyms collapsed per
    // Captain's lean. Inverse pairs flip the direction onto the kept containment
    // edge. Collapsing these lowers the edge-duplicate gate collision bound.
    // ── Tier 1: byte-identical shadows ──
    { kind: 'rename', from: 'insight_informs_opportunity_cross_domain', to: 'insight_informs_opportunity', requires_source_type: 'insight', requires_target_type: 'opportunity', reason: 'Duplicate collapse (UPG-677). Byte-identical shadow of insight_informs_opportunity; the suffixed twin never resolved.' },
    { kind: 'rename', from: 'product_experiences_incident_hierarchy', to: 'product_experiences_incident', requires_source_type: 'product', requires_target_type: 'incident', reason: 'Duplicate collapse (UPG-677). Byte-identical shadow of product_experiences_incident; the suffixed twin never resolved.' },
    { kind: 'rename', from: 'value_proposition_targets_persona_cross_domain', to: 'value_proposition_targets_persona', requires_source_type: 'value_proposition', requires_target_type: 'persona', reason: 'Duplicate collapse (UPG-677). Byte-identical shadow of value_proposition_targets_persona; the suffixed twin never resolved.' },
    { kind: 'rename', from: 'metric_measures_metric_cross_domain', to: 'metric_measures_metric', requires_source_type: 'metric', requires_target_type: 'metric', reason: 'Duplicate collapse (UPG-677). Byte-identical shadow of metric_measures_metric; the suffixed twin never resolved.' },
    { kind: 'rename', from: 'insight_validates_need_cross_domain', to: 'insight_validates_need', requires_source_type: 'insight', requires_target_type: 'need', reason: 'Duplicate collapse (UPG-677). Byte-identical shadow of insight_validates_need; the suffixed twin never resolved.' },
    { kind: 'rename', from: 'revenue_stream_measured_by_metric_cross_domain', to: 'revenue_stream_measured_by_metric', requires_source_type: 'revenue_stream', requires_target_type: 'metric', reason: 'Duplicate collapse (UPG-685 T0.1, 0.13.0 Wave 1). Cross-domain-classified shadow of revenue_stream_measured_by_metric (same measured_by/measures verbs); pickCanonicalEdge never returned it (the semantic key wins the pair; revenue_stream_drives_metric serves the cross-domain ask). Verb-preserving collapse onto the semantic canonical.' },
    // ── Tier 2: tense / suffix twins ──
    { kind: 'rename', from: 'metric_decomposed_into_metric', to: 'metric_decomposes_into_metric', requires_source_type: 'metric', requires_target_type: 'metric', reason: 'Duplicate collapse (UPG-677). Tense-twin of the active-voice metric_decomposes_into_metric.' },
    { kind: 'rename', from: 'product_decided_via_decision_hierarchy', to: 'product_decided_via_decision', requires_source_type: 'product', requires_target_type: 'decision', reason: 'Duplicate collapse (UPG-677). Suffix-twin of product_decided_via_decision.' },
    { kind: 'rename', from: 'support_ticket_reveals_need_cross_domain', to: 'support_ticket_reveals_need', requires_source_type: 'support_ticket', requires_target_type: 'need', reason: 'Duplicate collapse (UPG-677). Near-duplicate of support_ticket_reveals_need (whose reverse_verb is normalised to revealed_by).' },
    // ── Tier 3: near-synonyms (Captain's lean = collapse) ──
    { kind: 'rename', from: 'outcome_tracked_by_metric', to: 'outcome_measured_by_metric', requires_source_type: 'outcome', requires_target_type: 'metric', reason: 'Duplicate collapse (UPG-677). Near-synonym of outcome_measured_by_metric (keep measured_by, drop tracked_by).' },
    { kind: 'rename', from: 'key_result_tracked_by_metric', to: 'key_result_quantified_by_metric', requires_source_type: 'key_result', requires_target_type: 'metric', reason: 'Duplicate collapse (UPG-677). Near-synonym of key_result_quantified_by_metric (keep quantified_by, drop tracked_by).' },
    { kind: 'rename', from: 'business_model_reaches_via_distribution_channel', to: 'business_model_distributes_via_distribution_channel', requires_source_type: 'business_model', requires_target_type: 'distribution_channel', reason: 'Duplicate collapse (UPG-677). Near-synonym of business_model_distributes_via_distribution_channel (the Business Model Canvas Channels verb).' },
    // ── Inverse pairs: flip onto the kept containment edge ──
    // Flip guards reference the PRE-flip (original-edge) endpoints: the original
    // source becomes the new target after flip, so requires_source_type matches
    // the new edge's target_type and requires_target_type matches its source_type.
    { kind: 'rename', from: 'product_categorised_in_product_area', to: 'product_area_contains_product', flip: true, requires_source_type: 'product', requires_target_type: 'product_area', reason: 'Duplicate collapse (UPG-677). Inverse of the containment edge product_area_contains_product (product_area → product). Flip swaps endpoints; the "categorised in" read survives as the reverse_verb belongs_to.' },
    { kind: 'rename', from: 'changelog_documents_release', to: 'release_documented_in_changelog', flip: true, requires_source_type: 'changelog', requires_target_type: 'release', reason: 'Duplicate collapse (UPG-677). Inverse of the containment edge release_documented_in_changelog (release → changelog, matching hierarchy release: [changelog]). Flip swaps endpoints; the "changelog documents release" read survives as the reverse_verb documents.' },
  ],

  '0.9.2': [
    // (since v0.9.2, UPG-663) Journey-model disambiguation. A journey_phase
    // is a temporal BAND over the journey's single step timeline, not a
    // container that owns steps. The owning hierarchy edge
    // `journey_phase_has_step` is renamed to the non-owning
    // `journey_phase_spans_journey_step` (mirroring the marketing precedent
    // `customer_journey_stage_spans_journey_step`). Steps stay owned by the
    // journey via `user_journey_contains_journey_step` (the stable 0.1.0
    // spine), so each step keeps a single containment parent and the journey
    // renders one canonical step list. Endpoints are unchanged
    // (journey_phase → journey_step); only the edge key and its grammar
    // (verb has_step → spans, classification hierarchy → cross-domain) change.
    { kind: 'rename', from: 'journey_phase_has_step', to: 'journey_phase_spans_journey_step', requires_source_type: 'journey_phase', requires_target_type: 'journey_step', reason: 'Journey-model disambiguation (UPG-663): a phase spans steps, it does not own them. The phase to step edge becomes non-owning (verb has_step to spans), so the step keeps a single containment parent (the journey) and the journey has one canonical step list.' },
  ],

  '0.9.0': [
    // (since v0.9.0, UPG-660) theme → roadmap_theme. The four canonical edges that
    // touch the roadmap theme are renamed to the roadmap_theme form. Endpoint guards
    // reference the POST-migration (roadmap_theme) types; edge migration runs after
    // node migration, so by the time these rules apply the node has already been
    // renamed theme → roadmap_theme (UPG_MIGRATIONS['0.9.0']).
    { kind: 'rename', from: 'product_categorises_by_theme', to: 'product_categorises_by_roadmap_theme', requires_source_type: 'product', requires_target_type: 'roadmap_theme', reason: 'theme → roadmap_theme (UPG-660). Product categorises by the roadmap theme; edge key updated to the renamed target type.' },
    { kind: 'rename', from: 'roadmap_categorised_by_theme', to: 'roadmap_categorised_by_roadmap_theme', requires_source_type: 'roadmap', requires_target_type: 'roadmap_theme', reason: 'theme → roadmap_theme (UPG-660). Roadmap is categorised by the roadmap theme; edge key updated to the renamed target type.' },
    { kind: 'rename', from: 'theme_groups_feature', to: 'roadmap_theme_groups_feature', requires_source_type: 'roadmap_theme', requires_target_type: 'feature', reason: 'theme → roadmap_theme (UPG-660). The roadmap theme groups features; edge key updated to the renamed source type.' },
    { kind: 'rename', from: 'theme_spans_feature_area', to: 'roadmap_theme_spans_feature_area', requires_source_type: 'roadmap_theme', requires_target_type: 'feature_area', reason: 'theme → roadmap_theme (UPG-660). The roadmap theme spans feature areas; edge key updated to the renamed source type.' },
  ],

  '0.7.0': [
    // (since v0.7.0, UPG-571) story_statement → user_story re-canon. The four
    // canonical edges that touch the statement are renamed to the user_story
    // form. Endpoint guards reference the POST-migration (user_story) types;
    // edge migration runs after node migration, so by the time these rules
    // apply the statement node has already been renamed story_statement →
    // user_story (UPG_MIGRATIONS['0.7.0']).
    { kind: 'rename', from: 'task_implements_story_statement', to: 'task_implements_user_story', requires_source_type: 'task', requires_target_type: 'user_story', reason: 'story_statement → user_story (UPG-571). Task still implements the statement; edge key updated to the re-canonicalised target type.' },
    { kind: 'rename', from: 'epic_specified_by_story_statement', to: 'epic_specified_by_user_story', requires_source_type: 'epic', requires_target_type: 'user_story', reason: 'story_statement → user_story (UPG-571). Epics specify the statement; edge key updated to the re-canonicalised target type.' },
    { kind: 'rename', from: 'story_statement_verified_by_acceptance_criterion', to: 'user_story_verified_by_acceptance_criterion', requires_source_type: 'user_story', requires_target_type: 'acceptance_criterion', reason: 'story_statement → user_story (UPG-571). Acceptance criteria verify the statement; edge key updated to the re-canonicalised source type.' },
    { kind: 'rename', from: 'test_case_covers_story_statement', to: 'test_case_covers_user_story', requires_source_type: 'test_case', requires_target_type: 'user_story', reason: 'story_statement → user_story (UPG-571). Test cases cover the statement; edge key updated to the re-canonicalised target type.' },
  ],

  '0.4.0': [
    // ── hypothesis_claim → hypothesis reverse rename ───────────────
    // Every edge that was renamed FROM hypothesis_* TO hypothesis_claim_* in
    // v0.2.8 is renamed back. The canonical entity name
    // reverts; "claim" was redundant. hypothesis_evidence_* edges are NOT
    // renamed here; hypothesis_evidence is deprecated; use evidence +
    // hypothesis_has_evidence instead.
    { kind: 'rename', from: 'solution_proposes_hypothesis_claim', to: 'solution_proposes_hypothesis', requires_source_type: 'solution', requires_target_type: 'hypothesis', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    { kind: 'rename', from: 'hypothesis_claim_requires_experiment_plan', to: 'hypothesis_requires_experiment_plan', requires_source_type: 'hypothesis', requires_target_type: 'experiment_plan', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    { kind: 'rename', from: 'hypothesis_claim_planned_via_test_plan', to: 'hypothesis_planned_via_test_plan', requires_source_type: 'hypothesis', requires_target_type: 'test_plan', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    { kind: 'rename', from: 'hypothesis_claim_investigated_via_research_plan', to: 'hypothesis_investigated_via_research_plan', requires_source_type: 'hypothesis', requires_target_type: 'research_plan', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    { kind: 'rename', from: 'learning_updates_hypothesis_claim', to: 'learning_updates_hypothesis', requires_source_type: 'learning', requires_target_type: 'hypothesis', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    { kind: 'rename', from: 'learning_refines_hypothesis_claim', to: 'learning_refines_hypothesis', requires_source_type: 'learning', requires_target_type: 'hypothesis', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    { kind: 'rename', from: 'assumption_becomes_hypothesis_claim', to: 'assumption_becomes_hypothesis', requires_source_type: 'assumption', requires_target_type: 'hypothesis', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    { kind: 'rename', from: 'experiment_run_validates_hypothesis_claim', to: 'experiment_run_validates_hypothesis', requires_source_type: 'experiment_run', requires_target_type: 'hypothesis', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    { kind: 'rename', from: 'variant_tests_hypothesis_claim', to: 'variant_tests_hypothesis', requires_source_type: 'variant', requires_target_type: 'hypothesis', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    { kind: 'rename', from: 'feature_tests_hypothesis_claim', to: 'feature_tests_hypothesis', requires_source_type: 'feature', requires_target_type: 'hypothesis', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    { kind: 'rename', from: 'prototype_tests_hypothesis_claim', to: 'prototype_tests_hypothesis', requires_source_type: 'prototype', requires_target_type: 'hypothesis', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    { kind: 'rename', from: 'churn_reason_generates_hypothesis_claim', to: 'churn_reason_generates_hypothesis', requires_source_type: 'churn_reason', requires_target_type: 'hypothesis', reason: 'Reverse of the v0.2.8 rename. hypothesis_claim reverts to hypothesis.' },
    // ── hypothesis_evidence_* → drop (type deprecated) ────────────
    // hypothesis_evidence is deprecated at v0.4.0. All edges sourced from it
    // are dropped; the new pattern is hypothesis_has_evidence + evidence.direction.
    { kind: 'drop', from: 'hypothesis_evidence_supports_hypothesis_claim', reason: 'hypothesis_evidence deprecated; use hypothesis_has_evidence edge + evidence.direction="supports".' },
    { kind: 'drop', from: 'hypothesis_evidence_refutes_hypothesis_claim', reason: 'hypothesis_evidence deprecated; use hypothesis_has_evidence edge + evidence.direction="refutes".' },
    { kind: 'drop', from: 'hypothesis_evidence_derived_from_experiment_run', reason: 'hypothesis_evidence deprecated; evidence.source captures provenance.' },
    { kind: 'drop', from: 'hypothesis_evidence_derived_from_insight', reason: 'hypothesis_evidence deprecated; evidence.source captures provenance.' },
    { kind: 'drop', from: 'hypothesis_evidence_derived_from_observation', reason: 'hypothesis_evidence deprecated; evidence.source captures provenance.' },
    { kind: 'drop', from: 'hypothesis_evidence_derived_from_metric', reason: 'hypothesis_evidence deprecated; evidence.source captures provenance.' },
    // ── story_task → task rename ───────────────────────────────────
    { kind: 'rename', from: 'story_task_implements_story_statement', to: 'task_implements_story_statement', requires_source_type: 'task', requires_target_type: 'story_statement', reason: 'story_task collapsed into task; edge key updated to match canonical source type.' },
  ],

  '0.2.0': [
    // ── jtbd → job edge-key backfill ─────────────────────────────
    // The v0.2.0 entity-type rename `jtbd → job` (UPG_MIGRATIONS['0.2.0'])
    // shipped alongside an edge-key rename pass (the `_has_` → forward_verb
    // sweep) but the runtime translation rules were never landed in this
    // registry. Backfilled here so loaders chasing pre-0.2.0 data get a
    // single retarget hop instead of inventing their own map.
    //
    // Entity migration runs first, so source/target_type guards check the
    // post-migration types (`persona`, `user_journey`, `insight`, `job`,
    // etc.). The reason field cites the v0.2.0 CHANGELOG.
    {
      kind: 'rename',
      from: 'persona_has_jtbd',
      to: 'persona_pursues_job',
      requires_source_type: 'persona',
      requires_target_type: 'job',
      reason: 'Renamed alongside the jtbd→job entity rename and the _has_ → forward_verb sweep. The canonical edge changes verb from has→pursues; pursued_by reverses the relationship. Source remains persona; target migrates jtbd→job via UPG_MIGRATIONS["0.2.0"], so this rule fires after node migration completes.',
    },
    {
      kind: 'rename',
      from: 'journey_addresses_jtbd',
      to: 'user_journey_addresses_job',
      requires_source_type: 'user_journey',
      requires_target_type: 'job',
      reason: 'Renamed alongside the jtbd→job entity rename and the _has_ → forward_verb sweep. Source key prefix `journey` → `user_journey` matches the canonical entity name (no entity-type alias was needed because `journey` was never a registered type; it was an informal key prefix).',
    },
    {
      kind: 'rename',
      from: 'vp_addresses_jtbd',
      to: 'value_proposition_addresses_job',
      requires_source_type: 'value_proposition',
      requires_target_type: 'job',
      reason: 'Renamed alongside the jtbd→job entity rename. Source key prefix `vp` (acronym) expanded to canonical `value_proposition` matching the entity name.',
    },
    {
      kind: 'rename',
      from: 'finding_informs_jtbd',
      to: 'insight_informs_job',
      requires_source_type: 'insight',
      requires_target_type: 'job',
      reason: 'Source `finding` migrated to `insight` in v0.1.0 (UPG_MIGRATIONS["0.1.0"], with insight_level="finding" default); target `jtbd` migrated to `job` in v0.2.0. Edge-migration runs after both node migrations, so the rule fires once endpoints are post-migration.',
    },
    {
      kind: 'rename',
      from: 'quote_evidences_jtbd',
      to: 'quote_evidences_job',
      requires_source_type: 'quote',
      requires_target_type: 'job',
      reason: 'Renamed alongside the jtbd→job entity rename. Verb unchanged.',
    },
    {
      kind: 'rename',
      from: 'learning_validates_jtbd',
      to: 'learning_validates_job',
      requires_source_type: 'learning',
      requires_target_type: 'job',
      reason: 'Renamed alongside the jtbd→job entity rename. Verb unchanged.',
    },

    // ── v0.2.0 backfill: classification-suffix renames ─────────────
    // Pre-v0.2 era graphs annotated edge classification by appending
    // `_causal` / `_semantic` to the edge key. Classification became a
    // catalog property in v0.2.0 (`classification: 'causal' | 'semantic' |
    // 'hierarchy' | 'cross-domain'`), making the suffix redundant. The
    // stem keys are canonical; the suffixed forms are pure renames.
    {
      kind: 'rename',
      from: 'outcome_reveals_opportunity_causal',
      to: 'outcome_reveals_opportunity',
      reason: 'The `_causal` suffix annotated the edge\'s classification before v0.2 moved classification onto the catalog entry itself. Stem `outcome_reveals_opportunity` is canonical (classification: causal). Cross-graph audit: 15 edges across entopo.upg + unified-product-graph.upg.',
    },
    {
      kind: 'rename',
      from: 'persona_pursues_job_semantic',
      to: 'persona_pursues_job',
      reason: 'The `_semantic` suffix annotated the edge\'s classification before v0.2 moved classification onto the catalog entry itself. Stem `persona_pursues_job` is canonical (classification: semantic). Cross-graph audit: 6 edges in unified-product-graph.upg.',
    },

    // ── v0.2.0 cleanup: informal-edge drops ────────────────────────
    //
    // Pre-v0.2 graphs minted `product_contains_<entity>` edges to express
    // graph membership ("this entity belongs to this product"). v0.2 models
    // product membership through portfolio scope + the `upg_product`
    // frontmatter / file association, NOT through typed edges. The
    // `product_contains_*` family in the canonical catalog is intentionally
    // narrow: only `research_study` and `screen` (entities a product
    // structurally owns as artefacts). Everything else is graph-membership
    // noise being cleaned up at the v0.2.0 baseline.
    //
    // Doctrine: a product is a portfolio scope, not an entity container.
    // Structural containment is reserved for entities the product
    // physically owns. If a future v0.3 catalog elevates any of these to
    // canonical, the drop rules below become rename rules; today they're
    // drops with explicit reason.
    //
    // Sources: cross-graph audit (entopo.upg + unified-product-graph.upg)
    // surfaced these as `unmapped_legacy_edges` after `migrate_type` ran.
    {
      kind: 'drop',
      from: 'product_contains_persona',
      reason: 'Cleanup (since v0.2.0). Personas are scoped to a product via portfolio membership, not contained as a typed edge. v0.2 catalog reserves `product_contains_*` for structural artefacts (research_study, screen). Cross-graph audit: 8 edges total.',
    },
    {
      kind: 'drop',
      from: 'product_contains_market_segment',
      reason: 'Cleanup (since v0.2.0). Market segments belong to a product via portfolio scope. Not a structural containment relationship. Cross-graph audit: 5 edges.',
    },
    {
      kind: 'drop',
      from: 'product_contains_market_trend',
      reason: 'Cleanup (since v0.2.0). Market trends are environmental context, not contained artefacts. Cross-graph audit: 5 edges.',
    },
    {
      kind: 'drop',
      from: 'product_contains_proof_point',
      reason: 'Cleanup (since v0.2.0). Proof points belong to positioning / value proposition entities, not directly to the product as a container. Cross-graph audit: 7 edges.',
    },
    {
      kind: 'drop',
      from: 'product_contains_value_proposition',
      reason: 'Cleanup (since v0.2.0). Value propositions are scoped to a product via portfolio membership. Cross-graph audit: 3 edges.',
    },
    {
      kind: 'drop',
      from: 'product_contains_hypothesis',
      reason: 'Cleanup (since v0.2.0). Hypotheses (now hypothesis_claim post v0.2.8) belong to a product via portfolio scope. Cross-graph audit: 8 edges.',
    },
    {
      kind: 'drop',
      from: 'product_contains_positioning',
      reason: 'Cleanup (since v0.2.0). Positioning belongs to a product via portfolio scope. Cross-graph audit: 2 edges.',
    },
    // restoration: this edge is being re-introduced as a
    // canonical anchor. The v0.2.0 cleanup retired it on the assumption that
    // "competitive analysis is scoped to a product via portfolio scope", but
    // chain validation showed that assumption produces orphan analyses
    // for any single-product graph. The edge is back in `UPG_EDGE_CATALOG`
    // with `forward_verb: contains`, mirroring `product_contains_research_study`.
    // The historical drop rule is intentionally omitted; keeping it would
    // contradict the catalog and trip the `edge-migrations` invariant test
    // ("drop rule `from` keys are no longer in the canonical catalog").
    {
      kind: 'drop',
      from: 'product_contains_content_strategy',
      reason: 'Cleanup (since v0.2.0). Content strategy is scoped to a product via portfolio scope. Cross-graph audit: 1 edge.',
    },
    {
      kind: 'drop',
      from: 'product_contains_epic',
      reason: 'Cleanup (since v0.2.0). Epics belong to a product via portfolio scope and to features/work-units via hierarchy. Cross-graph audit: 1 edge.',
    },
    {
      kind: 'drop',
      from: 'product_contains_ideal_customer_profile',
      reason: 'Cleanup (since v0.2.0). ICPs are scoped to a product via portfolio scope. Cross-graph audit: 1 edge.',
    },
    {
      kind: 'drop',
      from: 'product_contains_insight',
      reason: 'Cleanup (since v0.2.0). Insights belong to a product via portfolio scope and to research artefacts via hierarchy. Cross-graph audit: 6 edges.',
    },
    {
      kind: 'drop',
      from: 'product_contains_competitor',
      reason: 'Cleanup (since v0.2.0). Competitors are environmental context scoped to a product, not contained artefacts. Cross-graph audit: 4 edges.',
    },
    {
      kind: 'drop',
      from: 'product_contains_learning',
      reason: 'Cleanup (since v0.2.0). Learnings belong to experiment_run via the canonical `experiment_run_produces_learning` edge, not directly to the product. Cross-graph audit: 2 edges.',
    },
    {
      kind: 'drop',
      from: 'hypothesis_contains_persona',
      reason: 'Cleanup (since v0.2.0). Hypotheses do not contain personas; the relationship is the reverse (personas surface needs that ground hypotheses). Cross-graph audit: 5 edges.',
    },
    {
      kind: 'drop',
      from: 'positioning_contains_content_piece',
      reason: 'Cleanup (since v0.2.0). Positioning is itself a content artefact; "containing" content pieces is informal. The canonical relationship runs through content_strategy. Cross-graph audit: 3 edges.',
    },
    {
      kind: 'drop',
      from: 'feature_contains_feature',
      reason: 'Cleanup (since v0.2.0). Feature self-nesting is handled via UPG_VALID_CHILDREN hierarchy (entity-tree relationship), not as a typed edge. Cross-graph audit: 4 edges.',
    },
    {
      kind: 'drop',
      from: 'parent_of',
      reason: 'Cleanup (since v0.2.0). Parent-child relationships are handled via UPG_VALID_CHILDREN hierarchy at the entity-tree level, not as a typed edge. Cross-graph audit: 1 edge.',
    },
    {
      kind: 'drop',
      from: 'related_to',
      reason: 'Cleanup (since v0.2.0). Generic "related to" has no canonical pair semantics; every real relationship has a specific verb in UPG_EDGE_CATALOG. Cross-graph audit: 1 edge.',
    },

    // ── v0.2.0 cleanup: informal-containment drops for v0.2.6+ children ─
    //
    // Same doctrine as above: `product_contains_*` is a portfolio
    // membership concern, not a typed edge. The original audit
    // covered the entity types in cross-graph data at the time. Several
    // canonical types introduced AFTER v0.2.0 (story_statement v0.2.7,
    // experiment_plan / experiment_run v0.2.6, plus metric / outcome /
    // decision which were always canonical but didn't surface in the
    // original audit) are surfaced by v0.3 round-trip fixtures with the
    // same informal `product_contains_<type>` shape. Add drops so they
    // canonicalise via the same `product is a scope, not a container`
    // rule. Sourced from `.upg/v03-roundtrip.upg` round-trip fixture.
    {
      kind: 'drop',
      from: 'product_contains_metric',
      reason: 'Cleanup follow-on (since v0.2.0; `product is a scope, not a container` doctrine). Metrics belong to a product via portfolio scope and to features/outcomes via canonical edges, not as a typed product-containment edge.',
    },
    {
      kind: 'drop',
      from: 'product_contains_outcome',
      reason: 'Cleanup follow-on (since v0.2.0; `product is a scope, not a container` doctrine). Outcomes belong to a product via portfolio scope; the canonical chain is opportunity_pursues_outcome / job_motivates_desired_outcome.',
    },
    {
      kind: 'drop',
      from: 'product_contains_decision',
      reason: 'Cleanup follow-on (since v0.2.0; `product is a scope, not a container` doctrine). Decisions belong to a product via portfolio scope and use the polymorphic decision_influences_node family for relational links.',
    },
    {
      kind: 'drop',
      from: 'product_contains_story_statement',
      reason: 'Cleanup follow-on (since v0.2.0; `product is a scope, not a container` doctrine). Story statements belong to a product via portfolio scope and to story_task via canonical implements edges. Type was introduced in v0.2.7 (post-original-audit).',
    },
    {
      kind: 'drop',
      from: 'product_contains_experiment',
      reason: 'Cleanup follow-on (since v0.2.0; `product is a scope, not a container` doctrine). Experiments (canonical alongside experiment_plan / experiment_run) belong to a product via portfolio scope, not as a typed containment edge.',
    },
    {
      kind: 'drop',
      from: 'product_contains_experiment_run',
      reason: 'Cleanup follow-on (since v0.2.0; `product is a scope, not a container` doctrine). Experiment runs belong to experiment_plan via canonical experiment_plan_ran_as_experiment_run, not directly to the product as a containment edge. Type was introduced in v0.2.6 (post-original-audit).',
    },

    // ── v0.2.0 backfill: persona-chain verb canonicalisation ──────────────
    //
    // The persona chain (v0.2 model) settled on the canonical edge
    // verbs `aspires_to` (persona → desired_outcome, hierarchy) and
    // `incurs` (persona → switching_cost, hierarchy). Some pre-canon
    // graphs minted these edges with informal verbs (`seeks`, `faces`)
    // that read more naturally but never made it into the catalog. Rename
    // so the canonical verb set stays the one authority and
    // validate_graph offers a single-hop fix.
    //
    // Sourced from `.upg/v03-roundtrip.upg` round-trip fixture.
    {
      kind: 'rename',
      from: 'persona_seeks_desired_outcome',
      to: 'persona_aspires_to_desired_outcome',
      requires_source_type: 'persona',
      requires_target_type: 'desired_outcome',
      reason: 'The canonical persona-chain verb is `aspires_to` (catalog: classification=hierarchy, source=persona, target=desired_outcome). `seeks` was an informal authoring shorthand; renaming surfaces a single canonical chain.',
    },
    {
      kind: 'rename',
      from: 'persona_faces_switching_cost',
      to: 'persona_incurs_switching_cost',
      requires_source_type: 'persona',
      requires_target_type: 'switching_cost',
      reason: 'The canonical persona-chain verb is `incurs` (catalog: classification=hierarchy, source=persona, target=switching_cost). `faces` was an informal authoring shorthand; renaming surfaces a single canonical chain.',
    },
  ],

  '0.2.7': [
    // ── split 1 closure: 18 experiment-edge retargets ──────────────
    // CHANGELOG v0.2.7 §"Breaking split 1 closure" enumerates
    // the full retarget list. Each rule guards on the canonical
    // post-migration endpoint type so legacy edges on already-migrated
    // nodes (post-applySplit) translate cleanly. `experiment` → `experiment_run`
    // 1→1 alias lives in UPG_MIGRATIONS["0.2.7"]; full plan+run split lives
    // in UPG_SPLIT_MIGRATIONS["0.2.6"].
    {
      kind: 'rename',
      from: 'hypothesis_requires_experiment',
      to: 'hypothesis_requires_experiment_plan',
      requires_source_type: 'hypothesis',
      requires_target_type: 'experiment_plan',
      reason: 'Split 1 closure (since v0.2.7). Hypothesis requires the planning artefact, not the run. Target retargets to experiment_plan; source retargets to hypothesis_claim in v0.2.8 (then registered as hypothesis_claim_requires_experiment_plan).',
    },
    {
      kind: 'rename',
      from: 'growth_campaign_tests_via_experiment',
      to: 'growth_campaign_tests_via_experiment_plan',
      requires_source_type: 'growth_campaign',
      requires_target_type: 'experiment_plan',
      reason: 'Split 1 closure (since v0.2.7). Growth campaigns reference the planning artefact (which experiments the campaign tests via).',
    },
    {
      kind: 'rename',
      from: 'pricing_strategy_tests_experiment',
      to: 'pricing_strategy_tests_experiment_plan',
      requires_source_type: 'pricing_strategy',
      requires_target_type: 'experiment_plan',
      reason: 'Split 1 closure (since v0.2.7). Pricing strategies reference the plan, not the run.',
    },
    {
      kind: 'rename',
      from: 'experiment_targets_behavioral_segment',
      to: 'experiment_plan_targets_behavioral_segment',
      requires_source_type: 'experiment_plan',
      requires_target_type: 'behavioral_segment',
      reason: 'Split 1 closure (since v0.2.7). Targeting is a planning concern (decided pre-execution), so retargets to experiment_plan.',
    },
    {
      kind: 'rename',
      from: 'experiment_produces_learning',
      to: 'experiment_run_produces_learning',
      requires_source_type: 'experiment_run',
      requires_target_type: 'learning',
      reason: 'Split 1 closure (since v0.2.7). Learnings are produced by the run (execution evidence), not the plan.',
    },
    {
      kind: 'rename',
      from: 'experiment_yields_evidence',
      to: 'experiment_run_yields_evidence',
      requires_source_type: 'experiment_run',
      requires_target_type: 'evidence',
      reason: 'Split 1 closure (since v0.2.7). Evidence yields from execution; retargets to experiment_run.',
    },
    {
      kind: 'rename',
      from: 'beta_program_runs_experiment',
      to: 'beta_program_runs_experiment_run',
      requires_source_type: 'beta_program',
      requires_target_type: 'experiment_run',
      reason: 'Split 1 closure (since v0.2.7). Beta programs run experiment_run instances (the actual executions).',
    },
    {
      kind: 'rename',
      from: 'experiment_tests_variant',
      to: 'experiment_run_tests_variant',
      requires_source_type: 'experiment_run',
      requires_target_type: 'variant',
      reason: 'Split 1 closure (since v0.2.7). Variant tests happen during run execution; retargets to experiment_run.',
    },
    {
      kind: 'rename',
      from: 'cohort_exposed_to_experiment',
      to: 'cohort_exposed_to_experiment_run',
      requires_source_type: 'cohort',
      requires_target_type: 'experiment_run',
      reason: 'Split 1 closure (since v0.2.7). Exposure is a run-time event; retargets to experiment_run.',
    },
    {
      kind: 'rename',
      from: 'experiment_tests_pricing_tier',
      to: 'experiment_run_tests_pricing_tier',
      requires_source_type: 'experiment_run',
      requires_target_type: 'pricing_tier',
      reason: 'Split 1 closure (since v0.2.7). Pricing tier tests happen during run execution; retargets to experiment_run.',
    },
    {
      kind: 'rename',
      from: 'dashboard_contains_experiment',
      to: 'dashboard_contains_experiment_run',
      requires_source_type: 'dashboard',
      requires_target_type: 'experiment_run',
      reason: 'Split 1 closure (since v0.2.7). Dashboards surface run results (the live data), not plans.',
    },
    {
      kind: 'rename',
      from: 'experiment_tests_feature',
      to: 'experiment_run_tests_feature',
      requires_source_type: 'experiment_run',
      requires_target_type: 'feature',
      reason: 'Split 1 closure (since v0.2.7). Feature tests happen during run execution; retargets to experiment_run.',
    },
    {
      kind: 'rename',
      from: 'experiment_measures_metric',
      to: 'experiment_run_measures_metric',
      requires_source_type: 'experiment_run',
      requires_target_type: 'metric',
      reason: 'Split 1 closure (since v0.2.7). Measurement is a run-time concern; retargets to experiment_run.',
    },
    {
      kind: 'rename',
      from: 'experiment_guards_metric',
      to: 'experiment_run_guards_metric',
      requires_source_type: 'experiment_run',
      requires_target_type: 'metric',
      reason: 'Split 1 closure (since v0.2.7). Guard-metric checks are a run-time concern; retargets to experiment_run.',
    },
    {
      kind: 'rename',
      from: 'experiment_measured_by_metric',
      to: 'experiment_run_measured_by_metric',
      requires_source_type: 'experiment_run',
      requires_target_type: 'metric',
      reason: 'Split 1 closure (since v0.2.7). Run-level measurement; retargets to experiment_run.',
    },
    // Edge consolidation: experiment_tested_via_experiment + duplicate
    // experiment_tests_experiment both collapse into the single canonical
    // experiment_run_tested_via_experiment_run.
    {
      kind: 'rename',
      from: 'experiment_tested_via_experiment',
      to: 'experiment_run_tested_via_experiment_run',
      requires_source_type: 'experiment_run',
      requires_target_type: 'experiment_run',
      reason: 'Split 1 closure (since v0.2.7). Multi-armed iterations and replications now express run-to-run; CHANGELOG v0.2.7: "the duplicate pair experiment_tested_via_experiment + experiment_tests_experiment consolidates into a single canonical experiment_run_tested_via_experiment_run".',
    },
    {
      kind: 'rename',
      from: 'experiment_tests_experiment',
      to: 'experiment_run_tested_via_experiment_run',
      requires_source_type: 'experiment_run',
      requires_target_type: 'experiment_run',
      reason: 'Split 1 closure (since v0.2.7). Near-duplicate of experiment_tested_via_experiment; consolidated into the canonical run-to-run testing edge.',
    },
    // Drop: experiment_tests_hypothesis superseded by canonical
    // experiment_run_validates_hypothesis (v0.2.6, then retargeted to
    // hypothesis_claim in v0.2.8).
    {
      kind: 'drop',
      from: 'experiment_tests_hypothesis',
      reason: 'Split 1 closure (since v0.2.7). Superseded by the canonical experiment_run_validates_hypothesis (causal) introduced in v0.2.6; the run is what validates the hypothesis, not an abstract experiment. CHANGELOG v0.2.7: "Edge dropped: experiment_tests_hypothesis is removed; superseded by the canonical experiment_run_validates_hypothesis (causal) introduced in v0.2.6."',
    },

    // ── split 2: 5 user_story-edge retargets + 2 drops ─────────────
    // CHANGELOG v0.2.7 §"Breaking split 2" enumerates the full
    // list. user_story → story_task 1→1 alias lives in
    // UPG_MIGRATIONS["0.2.7"]; full statement+task split lives in
    // UPG_SPLIT_MIGRATIONS["0.2.7"].
    {
      kind: 'rename',
      from: 'epic_specified_by_user_story',
      to: 'epic_specified_by_story_statement',
      requires_source_type: 'epic',
      requires_target_type: 'story_statement',
      reason: 'Epics own the spec (statement), not the work (task). Retargets to story_statement.',
    },
    {
      kind: 'rename',
      from: 'user_story_verified_by_acceptance_criterion',
      to: 'story_statement_verified_by_acceptance_criterion',
      requires_source_type: 'story_statement',
      requires_target_type: 'acceptance_criterion',
      reason: 'Acceptance criteria define what done looks like for the spec; retargets source to story_statement.',
    },
    {
      kind: 'rename',
      from: 'test_case_covers_user_story',
      to: 'test_case_covers_story_statement',
      requires_source_type: 'test_case',
      requires_target_type: 'story_statement',
      reason: 'Test cases cover the spec, not the work; retargets target to story_statement.',
    },
    // user_story_broken_into_task drops; consolidated into the implements edge.
    {
      kind: 'drop',
      from: 'user_story_broken_into_task',
      reason: 'Consolidated into the canonical implements edge (the relationship now flows from task to statement directly). CHANGELOG v0.2.7: "user_story_broken_into_task → dropped."',
    },
    // NOTE: the v0.2.7 drop of `task_implements_user_story` was REMOVED at v0.7.0
    // (UPG-571). Re-canonicalising story_statement → user_story makes
    // `task_implements_user_story` the canonical implements edge again, so the
    // name is no longer retired (a drop rule must never name a canonical edge).
    // Legacy pre-0.2.7 instances are reconnected by the v0.2.7 split's emitted
    // implements edge; any residual dangling edge is handled by
    // repair_dangling_edges.
  ],

  '0.2.8': [
    // ── split 3: 12 hypothesis-edge retargets + 1 drop ─────────────
    // PR #1151 diff of UPG_EDGE_CATALOG enumerates the full retarget list.
    // The 12 retargets all change a `hypothesis` endpoint (source or
    // target, occasionally both) to `hypothesis_claim`. The dropped edge
    // (`evidence_supports_hypothesis`) is structurally superseded by
    // `hypothesis_evidence_supports_hypothesis_claim` whose source is a
    // *different* entity type (`hypothesis_evidence`); so it cannot
    // round-trip via a flat rename and is registered as a drop.
    {
      kind: 'rename',
      from: 'solution_proposes_hypothesis',
      to: 'solution_proposes_hypothesis_claim',
      requires_source_type: 'solution',
      requires_target_type: 'hypothesis_claim',
      reason: 'Target hypothesis migrated to hypothesis_claim (the canonical "hypothesis" is now the claim); source unchanged.',
    },
    {
      kind: 'rename',
      from: 'hypothesis_requires_experiment_plan',
      to: 'hypothesis_claim_requires_experiment_plan',
      requires_source_type: 'hypothesis_claim',
      requires_target_type: 'experiment_plan',
      reason: 'Source hypothesis migrated to hypothesis_claim. (This edge was already retargeted target-side from experiment to experiment_plan in v0.2.7; v0.2.8 closes the source-side retarget.)',
    },
    {
      kind: 'rename',
      from: 'hypothesis_planned_via_test_plan',
      to: 'hypothesis_claim_planned_via_test_plan',
      requires_source_type: 'hypothesis_claim',
      requires_target_type: 'test_plan',
      reason: 'Source hypothesis migrated to hypothesis_claim; target unchanged.',
    },
    {
      kind: 'rename',
      from: 'hypothesis_investigated_via_research_plan',
      to: 'hypothesis_claim_investigated_via_research_plan',
      requires_source_type: 'hypothesis_claim',
      requires_target_type: 'research_plan',
      reason: 'Source hypothesis migrated to hypothesis_claim; target unchanged.',
    },
    {
      kind: 'rename',
      from: 'learning_updates_hypothesis',
      to: 'learning_updates_hypothesis_claim',
      requires_source_type: 'learning',
      requires_target_type: 'hypothesis_claim',
      reason: 'Target hypothesis migrated to hypothesis_claim; source unchanged.',
    },
    {
      kind: 'rename',
      from: 'assumption_becomes_hypothesis',
      to: 'assumption_becomes_hypothesis_claim',
      requires_source_type: 'assumption',
      requires_target_type: 'hypothesis_claim',
      reason: 'Target hypothesis migrated to hypothesis_claim; source unchanged.',
    },
    {
      kind: 'rename',
      from: 'experiment_run_validates_hypothesis',
      to: 'experiment_run_validates_hypothesis_claim',
      requires_source_type: 'experiment_run',
      requires_target_type: 'hypothesis_claim',
      reason: 'Closes the deferred target retarget from v0.2.6 (experiment_run_validates_hypothesis was introduced in v0.2.6 with target=hypothesis; v0.2.8 retargets to hypothesis_claim).',
    },
    {
      kind: 'rename',
      from: 'variant_tests_hypothesis',
      to: 'variant_tests_hypothesis_claim',
      requires_source_type: 'variant',
      requires_target_type: 'hypothesis_claim',
      reason: 'Target hypothesis migrated to hypothesis_claim; source unchanged.',
    },
    {
      kind: 'rename',
      from: 'churn_reason_generates_hypothesis',
      to: 'churn_reason_generates_hypothesis_claim',
      requires_source_type: 'churn_reason',
      requires_target_type: 'hypothesis_claim',
      reason: 'Target hypothesis migrated to hypothesis_claim; source unchanged.',
    },
    {
      kind: 'rename',
      from: 'learning_refines_hypothesis',
      to: 'learning_refines_hypothesis_claim',
      requires_source_type: 'learning',
      requires_target_type: 'hypothesis_claim',
      reason: 'Target hypothesis migrated to hypothesis_claim; source unchanged.',
    },
    {
      kind: 'rename',
      from: 'feature_tests_hypothesis',
      to: 'feature_tests_hypothesis_claim',
      requires_source_type: 'feature',
      requires_target_type: 'hypothesis_claim',
      reason: 'Target hypothesis migrated to hypothesis_claim; source unchanged.',
    },
    {
      kind: 'rename',
      from: 'prototype_tests_hypothesis',
      to: 'prototype_tests_hypothesis_claim',
      requires_source_type: 'prototype',
      requires_target_type: 'hypothesis_claim',
      reason: 'Target hypothesis migrated to hypothesis_claim; source unchanged.',
    },
    // Drop: evidence_supports_hypothesis superseded by canonical
    // hypothesis_evidence_supports_hypothesis_claim (different source type).
    {
      kind: 'drop',
      from: 'evidence_supports_hypothesis',
      reason: 'Superseded by the canonical hypothesis_evidence_supports_hypothesis_claim. The source type changes (evidence → hypothesis_evidence, the new dedicated P2 scored-assessment entity), so this is a structural drop rather than a key rename. Consumers walking dropped edges may opt to spawn hypothesis_evidence rows post-migration (out-of-band adapter logic). CHANGELOG v0.2.8: "Edge dropped: evidence_supports_hypothesis; superseded by the canonical hypothesis_evidence_supports_hypothesis_claim."',
    },

    // ── v0.2.8 closure: direction-flip rename ──────────────────────
    // Pre-v0.2 graphs minted `hypothesis_contains_feature` (source=hypothesis,
    // target=feature) to express "this hypothesis is tested by this feature".
    // The canonical relationship runs the OTHER way: `feature_tests_hypothesis_claim`
    // (source=feature, target=hypothesis_claim, classification: cross-domain)
    // because a feature is what tests a claim, not the reverse. After
    // node migration of hypothesis → hypothesis_claim, the legacy edge has
    // post-migration endpoints (source: hypothesis_claim, target: feature);
    // flipping endpoints + retyping produces the canonical edge.
    //
    // First real consumer of `flip: true` since the field was introduced
    // in.
    {
      kind: 'rename',
      from: 'hypothesis_contains_feature',
      to: 'feature_tests_hypothesis_claim',
      flip: true,
      requires_source_type: 'hypothesis_claim',
      requires_target_type: 'feature',
      reason: 'Closure (since v0.2.8). Pre-v0.2 graphs minted hypothesis_contains_feature (source=hypothesis → target=feature). The canonical relationship runs the other way: feature_tests_hypothesis_claim (source=feature → target=hypothesis_claim, cross-domain). Post node migration of hypothesis → hypothesis_claim, this rule flips endpoints and retypes. Cross-graph audit: 10 edges in entopo.upg.',
    },
  ],
}

/**
 * Get all edge migration rules between two versions, in version order.
 *
 * @example
 * // v0.2.0 backfill: six jtbd→job edge renames.
 * const rules = getUPGEdgeMigrations('0.0.0', '0.2.0')
 * rules.length // → 6
 * rules.every(r => r.kind === 'rename') // → true
 *
 * @example
 * // Full v0.2.x range: every rule from v0.2.0 + v0.2.7 + v0.2.8.
 * getUPGEdgeMigrations('0.0.0', '0.2.8').filter(r => r.kind === 'drop').length // → 4
 */
export function getUPGEdgeMigrations(
  fromVersion: string,
  toVersion: string,
): UPGEdgeMigration[] {
  const result: UPGEdgeMigration[] = []
  for (const [version, migrations] of Object.entries(UPG_EDGE_MIGRATIONS)) {
    if (versionInRange(version, fromVersion, toVersion)) {
      result.push(...migrations)
    }
  }
  return result
}

/**
 * Endpoint context for `migrateEdge` guard evaluation.
 *
 * Both `sourceType` and `targetType` should be the *post-migration* node
 * types (i.e. after `migrateNode` / `applySplit` has run on the endpoints).
 * When omitted, rules with `requires_source_type` / `requires_target_type`
 * guards are skipped (safer default; a guard that cannot be evaluated
 * does not fire).
 */
export interface UPGEdgeMigrationEndpoints {
  /** Post-migration source-node type (after `migrateNode` / `applySplit`). */
  sourceType?: string
  /** Post-migration target-node type (after `migrateNode` / `applySplit`). */
  targetType?: string
}

/**
 * Walk the edge-migration chain for `edge_type` until reaching a value that
 * exists in `UPG_EDGE_CATALOG` (the current canonical name) or the chain
 * dead-ends.
 *
 * **Why this exists.** `UPG_EDGE_MIGRATIONS` accumulates rules across the
 * spec's history. A single edge can be renamed multiple times, and the
 * direction can reverse (e.g. v0.2.8 renamed `solution_proposes_hypothesis`
 * → `solution_proposes_hypothesis_claim`, then v0.4.0 renamed the latter
 * back to the former). Naively picking "the first rule whose `from` matches"
 * surfaces a stale migration target. The validator's `edge_drift`
 * suggestions need to land on the *current* canonical name, not whatever
 * intermediate the chain hop happens to be next.
 *
 * **Resolution strategy.** Process all rules across all versions, sorted
 * latest-version first per `from` key. From a given starting key, follow
 * the highest-version `rename` rule whose `from` matches; if the result is
 * in `UPG_EDGE_CATALOG`, return it (canonical). Otherwise re-enter with the
 * new key and continue. Cycles are detected and broken to return a `cycle`
 * outcome rather than loop forever.
 *
 * **Return values.**
 *  - `{ kind: 'canonical', to }`: the walk landed on a key present in
 *    `UPG_EDGE_CATALOG`. Callers should suggest `to` as the migration target.
 *  - `{ kind: 'drop' }`: the walk encountered a `drop` rule. The edge has
 *    no canonical replacement.
 *  - `{ kind: 'dead_end', last }`: no rule matched and `last` is not in
 *    `UPG_EDGE_CATALOG`. The edge is non-canonical with no known migration
 *    target.
 *  - `{ kind: 'cycle', visited }`: a cycle was detected. Returned so
 *    callers can degrade gracefully. Should not happen in practice given
 *    the version-ordered structure of `UPG_EDGE_MIGRATIONS`.
 *
 * **Caller note.** Callers that already know `edge_type` is canonical
 * (i.e. `edge_type in UPG_EDGE_CATALOG`) should NOT call this helper;
 * there's nothing to suggest. The helper is for the case where the edge
 * type is deprecated and the caller needs to find its canonical successor.
 *
 * @example
 * // Single-hop walk: deprecated → canonical in one step.
 * walkMigrationChainToCanonical('solution_proposes_hypothesis_claim', UPG_EDGE_CATALOG)
 * // → { kind: 'canonical', to: 'solution_proposes_hypothesis' }
 *
 * @example
 * // Drop rule short-circuits the walk.
 * walkMigrationChainToCanonical('experiment_tests_hypothesis', UPG_EDGE_CATALOG)
 * // → { kind: 'drop' }
 *
 * @example
 * // Already-canonical edge type: returns dead_end because the helper is
 * // intended for non-canonical callers. The 'canonical' branch fires only
 * // when the chain ENDS on a catalog entry, not when it STARTS on one.
 * // (In practice the validator skips this case before calling.)
 */
export type WalkMigrationChainResult =
  | { kind: 'canonical'; to: string }
  | { kind: 'drop' }
  | { kind: 'dead_end'; last: string }
  | { kind: 'cycle'; visited: readonly string[] }

export function walkMigrationChainToCanonical(
  edge_type: string,
  catalog: Readonly<Record<string, unknown>>,
): WalkMigrationChainResult {
  // Build a deduplicated `from → highest-version rule` map. Sorting versions
  // ascending and overwriting ensures the latest rule wins per `from` key;
  // so a v0.4.0 rule beats a v0.2.8 rule for the same `from`.
  const latestRuleByFrom = new Map<string, UPGEdgeMigration>()
  const sortedVersions = Object.keys(UPG_EDGE_MIGRATIONS).sort(compareVersions)
  for (const version of sortedVersions) {
    for (const rule of UPG_EDGE_MIGRATIONS[version]) {
      latestRuleByFrom.set(rule.from, rule)
    }
  }

  const visited = new Set<string>()
  let current = edge_type
  // Cap iteration as a defensive guard against pathological chains.
  // No real migration chain exceeds a handful of hops; this just bounds the
  // worst case.
  const MAX_HOPS = 32
  for (let hop = 0; hop < MAX_HOPS; hop++) {
    if (visited.has(current)) {
      return { kind: 'cycle', visited: Array.from(visited) }
    }
    visited.add(current)

    // Canonical: we've landed on something the catalog declares; stop.
    if (current in catalog) {
      return { kind: 'canonical', to: current }
    }

    const rule = latestRuleByFrom.get(current)
    if (!rule) {
      // No rule for the current key and it's not canonical; chain dead-ends.
      return { kind: 'dead_end', last: current }
    }
    if (rule.kind === 'drop') {
      return { kind: 'drop' }
    }
    // rename: hop to the next key. Endpoint guards are intentionally
    // ignored here: the walker resolves the *type chain*, not whether a
    // specific edge instance can apply the rule. The caller (validator
    // edge_drift logic) only needs the final canonical name to suggest.
    current = rule.to
  }
  return { kind: 'dead_end', last: current }
}

/**
 * Apply edge migrations to a single edge.
 *
 * Returns:
 *   - the original edge (un-shaped, referentially equal) if no rule matched;
 *   - a new edge with retyped `type` (and possibly swapped `source`/`target`
 *     when the rule sets `flip: true`) if a `rename` rule matched;
 *   - `null` if a `drop` rule matched; caller should remove the edge.
 *
 * Endpoint guards (`requires_source_type` / `requires_target_type`) check
 * the *post-migration* endpoint types provided via `endpoints`. When
 * `endpoints` is omitted, guarded rules are skipped; callers running edge
 * migration in isolation (without endpoint type context) get only the
 * un-guarded rules.
 *
 * `T` is the caller's edge shape; only `type` is required. `source` and
 * `target` are touched only when `flip: true` and are otherwise preserved
 * verbatim.
 *
 * @example
 * // No rule matches; edge passes through unchanged.
 * const edge = { id: 'e1', type: 'persona_pursues_job' }
 * migrateEdge(edge, '0.2.0', '0.2.8') === edge // → true
 *
 * @example
 * // Rename rule matches with endpoint guards satisfied.
 * const legacy = { id: 'e2', source: 'p1', target: 'j1', type: 'persona_has_jtbd' }
 * const migrated = migrateEdge(legacy, '0.0.0', '0.2.0', { sourceType: 'persona', targetType: 'job' })
 * migrated?.type // → 'persona_pursues_job'
 *
 * @example
 * // Drop rule matches; null signals "remove this edge".
 * const dropped = { id: 'e3', type: 'experiment_tests_hypothesis' }
 * migrateEdge(dropped, '0.0.0', '0.2.7') // → null
 */
export function migrateEdge<T extends { type: string; source?: unknown; target?: unknown }>(
  edge: T,
  fromVersion: string,
  toVersion: string,
  endpoints?: UPGEdgeMigrationEndpoints,
): T | null {
  const rules = getUPGEdgeMigrations(fromVersion, toVersion)
  for (const rule of rules) {
    if (rule.from !== edge.type) continue
    if (rule.kind === 'drop') return null
    // rename: evaluate endpoint guards.
    if (rule.requires_source_type !== undefined) {
      if (endpoints?.sourceType !== rule.requires_source_type) continue
    }
    if (rule.requires_target_type !== undefined) {
      if (endpoints?.targetType !== rule.requires_target_type) continue
    }
    if (rule.flip) {
      return { ...edge, type: rule.to, source: edge.target, target: edge.source }
    }
    return { ...edge, type: rule.to }
  }
  return edge
}
